import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import html2canvas from 'html2canvas';
import { 
  ArrowLeft, Edit, Save, X, FileText, Truck, CheckCircle, 
  Calendar, Clock, User, Users, Building2, MapPin, Phone,
  AlertTriangle, Info, Download, Share2, MessageCircle, Timer,
  Stethoscope, Pill, Package, Plus, Search, Trash2,
  ChevronDown, ChevronUp, History
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { useToast } from '../../components/ui/use-toast';
import { Progress } from '../../components/ui/progress';
import DocumentUpload from '../../components/DocumentUpload';
import AsosDocumentUpload from '../../components/AsosDocumentUpload';
import Ek3DocumentUpload from '../../components/Ek3DocumentUpload';
import CasePdfGenerator from '../../components/CasePdfGenerator';

import { api, endpoints } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useHospitalAddresses, useTransportTypes } from '../../hooks/useLookups';

// Hasta adresleri ve sevk turleri artik veritabanindan cekiliyor - useHospitalAddresses / useTransportTypes hook'lari
// Yönetim: /lookup-definitions sayfasi (admin/supervisor erisimi)
const _REMOVED_HOSPITAL_ADDRESS_OPTIONS = [
  'Arnavutköy Devlet Hastanesi',
  'Dr. İsmail Niyazi Kurtulmuş Hastanesi',
  'Özel Avrupa Hospital Hastanesi',
  'Fatih Sultan Mehmet Eğitim Ve Araştırma Hastanesi',
  'Özel Memorial Ataşehir Hastanesi',
  'Özel Ataşehir Florence Nıghtıngale Hastanesi',
  'Özel Ataşehir Hastanesi',
  'Özel Avicenna Hastanesi Ataşehir (Anadolu Çınar)',
  'Özel Bayındır İçerenköy Hastanesi',
  'Özel Eren Hastanesi',
  'Avcılar Murat Kölük Devlet Hastanesi',
  'Özel Avcılar Anadolu Hastanesi',
  'Özel Avcılar Baypark Hastanesi',
  'Özel Avcılar Hospital',
  'Özel Medicana Hospitals Avcılar',
  'Bağcılar Eğitim Ve Araştırma Hastanesi',
  'Özel Medipol Mega Hastaneler Kompleksi',
  'Özel Safa Hastanesi',
  'Özel Yeni İklim Hastanesi',
  'Özel Bağcılar Medilife Hastanesi',
  'Özel Güneşli Erdem Hastanesi',
  'Özel Hastane34 Hastanesi',
  'Özel Hospitalist Hastanesi',
  'Özel İstanbul Medicine Hospital Hastanesi',
  'Bahçelievler Devlet Hastanesi',
  'İstanbul Özel Memorial Hizmet Hastanesi',
  'Özel A Hastanesi',
  'Özel Aile Hastanesi',
  'Özel Ataköy Hastanesi',
  'Özel İstanbul Göz Hastanesi',
  'Özel Median Hastanesi',
  'Özel Medical Park Bahçelievler Hastanesi',
  'Özel Medicana Hospitals Bahçelievler',
  'Özel Medicana Hospitals Haznedar Hastanesi',
  'Özel Memorial Bahçelievler Hastanesi',
  'Özel Nisa Hastanesi',
  'Özel Vital Hospital Hastanesi',
  'Özel Yenibosna Safa Hastanesi',
  'Bakırköy Dr. Sadi Konuk Eğitim Ve Araştırma Hastanesi',
  'Prof. Dr. Mazhar Osman Ruh Sağlığı Ve Sinir Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Osmaniye / Bakırköy Polikliniği',
  'Lepra Deri Ve Zührevi Hastalıkları Hastanesi',
  'Özel Acıbadem Bakırköy Hastanesi',
  'Özel Acıbadem İnternational Hastanesi',
  'Özel Çamlık Hastanesi',
  'Özel Dünya Göz Hastanesi Ataköy',
  'Özel Ethica İncirli Hastanesi',
  'Özel Yaşar Hastanesi',
  'Başakşehir Çam Ve Sakura Şehir Hastanesi',
  'Başakşehir Devlet Hastanesi',
  'Bayrampaşa Devlet Hastanesi',
  'Özel Baypark Hospital Hastanesi',
  'Özel Bayrampaşa Kolan Hospıtal Hastanesi',
  'Özel Göz Nurunu Koruma Vakfı Bayrampaşa Göz Hasta',
  'Beşiktaş Sait Çiftçi Devlet Hastanesi',
  'Özel Dentistanbul Diş Hastanesi',
  'Özel Dünya Göz Hastanesi Etiler',
  'Özel Estethica Levent Hastanesi',
  'Özel Etiler Hastanesi',
  'Özel Gayrettepe Florance Nightingale Hastanesi',
  'Özel Liv Hospital Ulus Hastanesi',
  'Özel Ota-Jinemed Hastanesi',
  'Beykoz Devlet Hastanesi',
  'Özel Medistate Kavacık Hastanesi',
  'Beylikdüzü Ağız Ve Diş Sağlığı Merkezi',
  'Özel Beylikdüzü Kolan Hospital Hastanesi',
  'Özel Beylikdüzü Medilife Hastanesi',
  'Özel Birinci Göz Hastanesi',
  'Özel Beylikdüzü Medicana Hastanesi',
  'Beyoğlu Göz Eğitim Ve Araştırma Hastanesi',
  'Taksim Eğitim Ve Araştırma Hastanesi',
  'Özel Okmeydanı Hastanesi',
  'Özel Avusturya Sen Jorj Hastanesi',
  'Beylikdüzü Devlet Hastanesi',
  'Büyükçekmece Mimar Sinan Devlet Hastanesi',
  'Özel Büyükçekmece Kolan Hospital Hastanesi',
  'Çatalca İlyas Çokay Devlet Hastanesi',
  'Çekmeköy Devlet Hastanesi',
  'Esenler Kadın Doğum Ve Çocuk Hastalıkları Hastanesi',
  'Özel Avicenna Hastanesi',
  'Özel Esenler Ensar Hastanesi Avicenna',
  'Özel Güney Hastanesi',
  'Esenyurt Necmi Kadıoğlu Devlet Hastanesi',
  'Özel Doğa Hospital Hastanesi',
  'Özel Esencan Hastanesi',
  'Özel Reyap İstanbul Hastanesi',
  'Eyüpsultan Devlet Hastanesi',
  'Özel Alibey Hospital Hastanesi',
  'Haseki Eğitim Ve Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Samatya Polikliniği',
  'Bezmialem Vakıf Üniversitesi Tıp Fakültesi Hastanesi',
  'İstanbul Üniversitesi Cerrahpaşa Tıp Fakültesi Hastanesi',
  'Özel Balat Or-Ahayim Musevi Hastanesi Vakfı İktisadi İ',
  'Özel Çapa Hastanesi',
  'Özel Çapa Medilife Hastanesi',
  'Özel Fatih Hastanesi',
  'Özel Medikal Park Hospital Hastanesi',
  'Gaziosmanpaşa Eğitim Ve Araştırma Hastanesi',
  'Gaziosmanpaşa Eğitim Ve Araştırma Fizik Tedavi Rehabilitasyon Hastanesi',
  'Özel Avrupa Şafak Hastanesi',
  'Özel Başarı Hastanesi',
  'Özel Bilge Hastanesi',
  'Özel Duygu Hastanesi',
  'Özel Yıldız Academıa Hastanesi',
  'Yeni Yüzyıl Üniversitesi Özel Gaziosmanpaşa Hastanesi',
  'Özel Asya Hastanesi',
  'Özel Avrasya Hastanesi Gaziosmanpaşa',
  'Güngören Ağız Ve Diş Sağlığı Merkezi',
  'Özel Güngören Hastanesi',
  'Özel İlgi Hastanesi',
  'Özel Meltem Hastanesi Ve Doğum Hastanesi',
  'Erenköy Fizik Tedavi Ve Rehabilitasyon Hastanesi',
  'Göztepe Prof. Dr. Süleyman Yalçın Şehir Hastanesi',
  'Erenköy Ruh Ve Sinir Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Acıbadem Hastanesi',
  'Özel Acıbadem Kozyatağı Hastanesi',
  'Özel Çağıner Hastanesi',
  'Özel Doktor Nazif Bağrıaçık Kadıköy Hastanesi',
  'Özel Göztepe Hastanesi',
  'Özel İstanbul Medipol Hastanesi',
  'Özel Kadıköy Florance Nightingale Hastanesi',
  'Özel Kozyatağı Central Hospital',
  'Özel Medical Park Göztepe Hastanesi Kopmleksi (Genel)',
  'Özel Medicana Kadıköy Hastanesi',
  'Kağıthane Devlet Hastanesi',
  'Özel Avicenna Hastanesi Gültepe',
  'Özel Derindere Hastanesi',
  'Özel Levent Hastanesi',
  'Kartal Dr. Lütfi Kırdar Şehir Hastanesi',
  'Kartal Koşuyolu Yüksek İhtisas Eğitim Ve Araştırma Hastanesi',
  'Özel Vera Hastanesi',
  'Türk Kızılayı Özel Kartal Hastanesi',
  'Özel Avicenna Umut Hastanesi',
  'Özel Birlik Hastanesi',
  'Özel İstanbul İbni Sina Hastanesi',
  'Kanuni Sultan Süleyman Eğitim Ve Araştırma Hastanesi',
  'Mehmet Akif Ersoy Göğüs Kalp Ve Damar Cerrahisi Eğitim Araştırma Hastanesi',
  'Özel Akgün Tem Hastanesi',
  'Özel Batı Bahat Hospital Hastanesi',
  'Özel Doğan Hastanesi',
  'Özel Dr.Sadık Ahmet Hastanesi',
  'Özel Halkalı Kent Hastanesi',
  'Özel İstanbul Hospital',
  'Özel Rumeli Hospital Hastanesi',
  'Süreyyapaşa Göğüs Hastalıkları Ve Göğüs Cerrahisi Eğitim Araştırma Hastanesi',
  'Maltepe Devlet Hastanesi',
  'Fatih Üniversitesi Tıp Fakültesi Maltepe Hastanesi',
  'İstanbul Meslek Hastalıkları Hastanesi',
  'Maltepe Üniversitesi Tıp Fakültesi Hastanesi',
  'Özel Maltepe Bölge ( Dünya) Hastanesi',
  'Özel Maltepe Ersoy Hastanesi',
  'Özel Mehmet Toprak Hastanesi',
  'Özel Delta Hospital',
  'Özel İstanbul Onkoloji Hastanesi',
  'Marmara Universitesi İstanbul Pendik Eğitim Ve Araştırma Hastanesi',
  'Pendik Devlet Hastanesi',
  'Özel Pendik Bölge Hastanesi',
  'Özel Pendik Hospital Hastanesi',
  'Özel Remedy Hospital Hastanesi',
  'Özel Şifa Hastanesi',
  'Özel Vm Medicalpark Pendik Hastanesi',
  'Özel Yüzyıl Hastanesi Pendik',
  'Özel Adatıp Kurtköy Hastanesi',
  'Özel Emsey Hospital Hastanesi',
  'Özel Kurtköy Ersoy Hastanesi',
  'Prof. Dr. Feriha Öz Acil Durum Hastanesi',
  'Sancaktepe Şehit Prof. Dr. İlhan Varank Eğitim Ve Araştırma Hastanesi',
  'Sancaktepe Ağız Ve Diş Sağlığı Merkezi',
  'Özel Via Hospital Sancaktepe',
  'Özel İstanbul Bölge Hastanesi',
  'İstinye Devlet Hastanesi',
  'Sarıyer İsmail Akgün Devlet Hastanesi',
  'Sarıyer Hamidiye Etfal Eğitim Araştırma Çayırbaşı Ek Hizmet Binası',
  'Baltalımanı Metin Sabancı Kemik Hastalıkları Eğitim Araştırma Hastanesi',
  'Özel Acıbadem Maslak Hastanesi',
  'Marmara Ceza İnfaz Kurumu Devlet Hastanesi',
  'Silivri Devlet Hastanesi',
  'Özel Silivri Kolan Hospital Hastanesi',
  'Özel Silivri Medical Park Hastanesi',
  'Sultanbeyli Devlet Hastanesi',
  'Özel Ersoy Hastanesi',
  'Özel Saygı Hastanesi',
  'Lütfiye Nuri Burat Devlet Hastanesi',
  'Özel Bahat Hospital Hastanesi',
  'Özel Gazi Hastanesi',
  'Özel Medigold Sultan Hastanesi',
  'Sile Devlet Hastanesi',
  'Okmeydanı Eğitim Ve Araştırma Hastanesi',
  'Prof. Dr. Cemil Taşcıoğlu Şehir Hastanesi',
  'Sisli Hamidiye Etfal Eğitim Ve Araştırma Hastanesi',
  'Özel Acıbadem Taksim Hastanesi',
  'Özel Dora Hospital Hastanesi',
  'Özel Fransız Lape Hastanesi',
  'Özel İstanbul Cerrahi Hastanesi',
  'Özel İstanbul Florence Nıghtıngale Hastanesi',
  'Özel Kadıoğlu Hastanesi',
  'Özel Mecidiyeköy Çevre Hastanesi',
  'Özel Memorial Şişli Hastanesi',
  'Özel Nişantaşı Hastanesi',
  'Özel Osmanoğlu Hastanesi',
  'Özel Surp Agop Hastanesi (Taksim Vakıf Ermeni)',
  'Özel Şişli Florance Nightingale Hastanesi',
  'Özel Şişli Kolan İnternatıonal Hospital Hastanesi',
  'Özel Türkiye Gazetesi Hastanesi',
  'Özel Vehbi Koç Vakfı Amerikan Hastanesi',
  'Tuzla Devlet Hastanesi',
  'Özel Tuzla Hastanesi',
  'Özel Gsm Gisbir Sağlık Merkezi Hastanesi',
  'Ümraniye Eğitim Ve Araştırma Hastanesi',
  'Özel Hisar Çamlıca Hastanesi',
  'Özel Hisar İntercontinental Hospital Hastanesi',
  'Özel Np İstanbul Beyin Hastanesi',
  'Özel Afiyet Hastanesi',
  'Özel Atlas Hastanesi',
  'Özel Erdem Hastanesi',
  'Üsküdar Devlet Hastanesi',
  'Dr. Siyami Ersek Göğüs Kalp Ve Damar Cerrahisi Eğitim Ve Araştırma Hastanesi',
  'Haydarpaşa Numune Eğitim Ve Araştırma Hastanesi',
  'Sultan 2.Abdüıhamid Han Eğitim Ve Araştırma Hastanesi',
  'Zeynep Kamil Kadın Ve Çocuk Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Academic Hospital Hastanesi',
  'Özel Acıbadem Altunizade Hastanesi',
  'Özel Çamlıca Erdem Hastanesi',
  'Özel Medıvıa Hospıtal Hastanesi',
  'Özel Medicana Hospitals Çamlıca Hastanesi',
  'Özel Moodist Hastanesi',
  'Özel Üsküdar Hospitaltürk Hastanesi',
  'Özel Üsküdar Anadolu Hastanesi',
  'Özel Yunus Emre Hastanesi',
  'Yedikule Göğüs Hastalıkları Ve Göğüs Cerrahisi Eğitim Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Zeytinburnu Polikliniği',
  'Süleymaniye Kadın Doğum Ve Çocuk Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Avrasya Hastanesi',
  'Özel İstanbul Balıklı Rum Hastanesi',
  'Özel Sante Plus Hastanesi',
  'Özel Yedikule Surp Pırgiç Ermeni Hastanesi',
  'Ataşehir Medicana Hastanesi',
  'Beykent Üniversite Hastanesi',
  'Ataköy Medicana Hastanesi',
  'Zincirlikuyu Medicana Hastanesi',
  'Silivri Anadolu Hastanesi',
  'Haribo',
  'Ceren Ofset',
  'Galataport',
  'Kuyumcukent',
  'Botaş',
  'Kumsal Bakımevi',
  'Avrupa Diyaliz Merkezi Arnavutköy',
  'Mall Of Avm',
  'Saadet Gıda',
  'İstanbul Fizik Tedavi Ve Rehabilitasyon Eğitim Ve Araştırma Hastanesi',
  'İstinye Liv Hospital',
  'Arnavutköy Belediyesi',
  'Kale Kayışlar',
  'Erse Kablo',
  'Şenyayla',
  'Elif Plastik',
  'Öztıryakiler',
  'Colins',
  'Güngören Belediyesi',
  'Etra Sağlık Hizmetleri',
  'Seyrantepe Hamidiye Etfal Ek Hizmet Binası(Ek Kampüs)',
  'Zeytinburnu Avrasya Hastanesi',
  'Dr Sadi Konuk Bahçelievler Diş Hastanesi',
  'Pendik Kaynarca Semt Polikliniği',
  'Asaf Ataseven Ek Hizmet Binası',
  'Özel Medipol Bahçelievler Hastanesi',
  'Acıbadem Atakent Hastanesi',
  'Sabiha Gökçen Hava Limanı',
  'İstinye Üniversitesi Medical Park Gaziosmanpaşa',
  'Koç Üniversite Hastanesi',
  'Dragos Bezmialem Hastanesi',
  'İstinye Üniversitesi Topkapı Sağlık Bilimleri Hastanesi',
  'Kaya Ramada Otel',
  'Tüyap',
  'Meva Bakımevi',
  'Avcılar Oksimer Merkezi',
  'Medicell Tıp Merkezi',
  'Darülaceze',
  'Ev Adresi',
  'Diğer'
]; // Bu sabit artık kullanılmıyor, sadece referans için tutuluyor

// Status options
const STATUS_OPTIONS = [
  { value: 'initiated', label: 'Başlatıldı', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'Devam Ediyor', color: 'bg-yellow-500' },
  { value: 'completed', label: 'Tamamlandı', color: 'bg-green-500' },
  { value: 'cancelled', label: 'İptal Edildi', color: 'bg-red-500' }
];

// Sevk turleri artik veritabanindan cekiliyor - useTransportTypes() hook'u
// Bu sabit artik kullanilmiyor, sadece referans icin tutuluyor
const _REMOVED_TRANSPORT_TYPES = [
  { value: 'EVE_NAKIL', label: 'Eve Nakil' },
  { value: 'SAGLIK_TEDBIRI', label: 'Sağlık Tedbiri' },
  { value: 'KONSULTASYON', label: 'Konsültasyon' },
  { value: 'SERVIS_SEVKI', label: 'Servis Sevki' },
  { value: 'DIYALIZ_SEVK', label: 'Diyaliz Sevk' },
  { value: 'TIBBI_TETIK', label: 'Tıbbi Tetik' },
  { value: 'EVE_NAKIL_EVDE_SAGLIK', label: 'Eve Nakil/Evde Sağlık' },
  { value: 'HASTANEYE_SEVK_EVDE_SAGLIK', label: 'Hastaneye Sevk/Evde Sağlık' },
  { value: 'VAKA_GOREV_IPTAL', label: 'Vaka/Görev İptal' },
  { value: 'SOSYAL_ENDIKASYON', label: 'Sosyal Endikasyon' },
  { value: 'NICU_SEVK', label: 'NICU Sevk' },
  { value: 'KYB_SEVK', label: 'KYB Sevk' },
  { value: 'GYB_SEVK', label: 'GYB Sevk' },
  { value: 'NAKIL_RED', label: 'Nakil Red' },
  { value: 'PICU_SEVK', label: 'PICU Sevk' },
  { value: 'BAKIMEVI_SEVK', label: 'BAKIMEVİ Sevk' }
];

// Faturalanılacak kurum seçenekleri - Dinamik olarak institutions tablosundan çekilecek

const CaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Lookup verileri veritabanından çekiliyor
  const { data: hospitalAddresses = [] } = useHospitalAddresses();
  const { data: transportTypesList = [] } = useTransportTypes();
  
  // External institution and external_presidency users can only view, not edit (except stage 1 + EK-3 for presidency)
  const canEdit = !['external_institution', 'external_presidency', 'external_hospital'].includes(user?.role);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isSupervisorOrAdmin = ['admin', 'super_admin', 'supervisor'].includes(user?.role);
  const isParamedic = user?.role === 'paramedic';
  const canEditFull = canEdit && !isParamedic; // Paramedic can only edit limited fields
  const isPresidencyUser = user?.role === 'external_presidency';
  // Presidency users can edit stage 1 of their own cases
  const canEditStage1 = canEdit || isPresidencyUser;

  // Runtime error state
  const [runtimeError, setRuntimeError] = useState(null);
  const [billingInstitutions, setBillingInstitutions] = useState([]);
  const [billingInstitutionIdMap, setBillingInstitutionIdMap] = useState({});
  const [institutionsData, setInstitutionsData] = useState([]);
  const [institutionZones, setInstitutionZones] = useState([]);

  // Vakayı Veren states
  const [caseGiverType, setCaseGiverType] = useState('person');
  const [selectedPresidency, setSelectedPresidency] = useState(null);
  const [selectedPresidencyHospital, setSelectedPresidencyHospital] = useState('');
  const [presidencyHospitals, setPresidencyHospitals] = useState([]);
  const [allHospitals, setAllHospitals] = useState([]);
  const [presidencies, setPresidencies] = useState([]);
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  
  // PDF sharing states
  const [pdfGeneratorRef, setPdfGeneratorRef] = useState(null);
  const [pdfStatus, setPdfStatus] = useState(null);

  // Activity log toggle
  const [showActivityLog, setShowActivityLog] = useState(false);

  // Medical data states for stage3
  const [caseDiagnoses, setCaseDiagnoses] = useState([]);
  const [caseMedications, setCaseMedications] = useState([]);
  const [caseSupplies, setCaseSupplies] = useState([]);
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [supplySearch, setSupplySearch] = useState('');
  const [showSupplyDropdown, setShowSupplyDropdown] = useState(false);
  
  // PDF generator ref callback
  const setPdfGeneratorRefCallback = useCallback((ref) => {
    setPdfGeneratorRef(ref);
  }, []);

  // Error boundary effect
  useEffect(() => {
    const handleError = (event) => {
      console.error('Runtime error in CaseDetailPage:', event.error);
      setRuntimeError(event.error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Kurumları fetch et
  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await api.get('/institutions');
        if (response.data.success) {
          const institutions = response.data.data || [];
          setInstitutionsData(institutions);
          setBillingInstitutions(institutions.map(inst => inst.name));
          const idMap = {};
          institutions.forEach(inst => {
            if (inst.name) {
              idMap[inst.name] = inst.id;
            }
          });
          setBillingInstitutionIdMap(idMap);
        }
      } catch (error) {
        console.error('Kurumlar yüklenirken hata:', error);
        // Fallback olarak eski listeyi kullan
        setBillingInstitutions([
          'GÜNEY 1. BAŞKANLIK KRİZ MASASI',
          'KUZEY 2. BAŞKANLIK KRİZ MASASI',
          'BAKIRKÖY 3. BAŞKANLIK KRİZ MASASI',
          'BEYOĞLU 4. BAŞKANLIK KRİZ MASASI',
          'FATİH 5. BAŞKANLIK KRİZ MASASI',
          'ÇEKMECE 6. BAŞKANLIK KRİZ MASASI',
          'AVCILAR ANADOLU HASTANESİ',
          'AVCILAR MEDİCANA HASTANESİ',
          'BOTAŞ',
          'SİLİVRİ ANADOLU HASTANESİ',
          'KUMSAL BAKIMEVİ',
          'BEYLİKDÜZÜ MEDİCANA HASTANESİ',
          'BEYKENT ÜNİVERSİTE HASTANESİ',
          'HARİBO',
          'CEREN OFSET',
          'GALATAPORT',
          'KUYUMCUKENT',
          'BAHÇELİEVLER MEDİCANA HASTANESİ',
          'ATAKÖY MEDİCANA HASTANESİ',
          'BEZMİALEM ÜNİVERSİTE HASTANESİ',
          'ZİNCİRLİKUYU MEDİCANA HASTANESİ',
          'KADIKÖY MEDİCANA HASTANESİ',
          'ATAŞEHİR MEDİCANA HASTANESİ',
          'ÇAMLICA MEDİCANA HASTANESİ',
          'YENİBOSNA SAFA HASTANESİ',
          'DİĞER',
          'ARNAVUTKÖY BELEDİYESİ',
          'İSTİNYE LİV HOSPİTAAL',
          'KALE KAYIŞLAR',
          'ERSE KABLO',
          'ŞENYAYLA',
          'ELİF PLASTİK',
          'ÖZTİRYAKİLER',
          'COLİNS',
          'ETRA SAĞLIK HİZMETLERİ',
          'MALL OF AVM',
          'SAADET GIDA',
          'GÜNGÖREN BELEDİYESİ',
          'ÖZGÜN BAKIMEVİ'
        ]);
      }
    };
    
    fetchInstitutions();
  }, []);

  // Fetch hospitals and presidencies for vakayı veren
  useEffect(() => {
    const fetchHospitalsAndPresidencies = async () => {
      try {
        const [hospitalsRes, institutionsRes] = await Promise.all([
          api.get('/hospitals'),
          api.get('/institutions')
        ]);
        const hospitals = hospitalsRes.data?.data || [];
        const institutions = institutionsRes.data?.data || [];
        setAllHospitals(hospitals);
        const presFiltered = institutions.filter(inst =>
          inst.name && inst.name.toUpperCase().includes('BAŞKANLIK')
        );
        setPresidencies(presFiltered);
      } catch (error) {
        console.error('Hastane/Başkanlık yüklenirken hata:', error);
      }
    };
    fetchHospitalsAndPresidencies();
  }, []);

  // Fetch active organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await api.get('/organizations/lookup/active');
        setActiveOrganizations(res.data?.data || []);
      } catch (error) {
        console.error('Organizasyonlar yüklenirken hata:', error);
      }
    };
    fetchOrganizations();
  }, []);

  // Filter hospitals when presidency changes
  useEffect(() => {
    if (selectedPresidency && allHospitals.length > 0) {
      const filtered = allHospitals.filter(h => h.institution_id === selectedPresidency.id);
      setPresidencyHospitals(filtered);
    } else {
      setPresidencyHospitals([]);
    }
  }, [selectedPresidency, allHospitals]);

  // NOTE: caseGiverType init effect moved after caseData useQuery declaration to avoid TDZ

  // Helper function to get staff names with fallback
  const getStaffName = (caseData, staffNumber) => {
    if (!caseData) return null;
    
    // First try direct field
    const directField = caseData[`staff_${staffNumber}_name`];
    if (directField) return directField;
    
    // Fallback to extra_data
    if (caseData.extra_data) {
      try {
        const extraData = JSON.parse(caseData.extra_data);
        return extraData[`staff_${staffNumber}`] || extraData[`staff_${staffNumber}_name`];
      } catch (e) {
        // Silently fail, don't spam console
      }
    }
    
    return null;
  };

  // Enhanced helper function to get staff names including form values
  const getCurrentStaffName = (staffNumber) => {
    // Watch form values
    const formValue = watch(`staff_${staffNumber}_name`);
    
    // Return form value if available, otherwise fallback to caseData
    return formValue || getStaffName(caseData, staffNumber);
  };

  // Enhanced helper function to get ambulance plate including form values
  const getCurrentAmbulancePlate = () => {
    // Watch form value
    const formValue = watch('ambulance_plate');
    
    // Return form value if available, otherwise fallback to caseData
    return formValue || (caseData ? caseData.ambulance_plate : null);
  };

  // Helper function to check if stage 3 has any data
  const hasStage3Data = (caseData) => {
    if (!caseData) return false;
    
    return !!(
      caseData.notification_time || caseData.arrival_time || caseData.delivery_time ||
      getCurrentAmbulancePlate() || getCurrentStaffName(1) || getCurrentStaffName(2) || 
      getCurrentStaffName(3) || caseData.handover_person || caseData.handover_title || 
      caseData.special_notes || caseData.result_description || caseData.completion_notes
    );
  };

  const [editingSection, setEditingSection] = useState(null);
  
  // Hospital search states (for patient address)
  const [hospitalSearchTerm, setHospitalSearchTerm] = useState('');
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [hospitalHighlightIndex, setHospitalHighlightIndex] = useState(-1);
  const hospitalDropdownRef = useRef(null);
  
  // Destination address search states (for stage2)
  const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  const [destinationHighlightIndex, setDestinationHighlightIndex] = useState(-1);
  const destinationDropdownRef = useRef(null);
  
  // Payment states
  const [isPaidTransport, setIsPaidTransport] = useState(false);
  
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      patient_nationality_type: 'TC'
    }
  });
  const selectedNationalityType = watch('patient_nationality_type');
  const [isDocumentDownloading, setIsDocumentDownloading] = useState(false);
  const [isAsosDocumentDownloading, setIsAsosDocumentDownloading] = useState(false);

  // Auto-calculate age/birth year
  const calculateAge = (birthYear) => {
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(birthYear);
  };

  const calculateBirthYear = (age) => {
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(age);
  };

  // Auto-calculate functions for onBlur events
  const handleBirthYearBlur = (e) => {
    const birthYear = e.target.value;
    if (birthYear && birthYear.length === 4) {
      const calculatedAge = calculateAge(birthYear);
      if (calculatedAge >= 0 && calculatedAge <= 150) {
        setValue('patient_age', calculatedAge);
      }
    }
  };

  const handleAgeBlur = (e) => {
    const age = e.target.value;
    if (age && parseInt(age) >= 0 && parseInt(age) <= 150) {
      const calculatedBirthYear = calculateBirthYear(age);
      if (calculatedBirthYear >= 1900 && calculatedBirthYear <= new Date().getFullYear()) {
        setValue('patient_birth_year', calculatedBirthYear);
      }
    }
  };

  // Hospital search functions (hospitalAddresses veritabanından çekiliyor)
  const handleHospitalSearch = (e) => {
    const value = e.target.value;
    setHospitalSearchTerm(value);
    
    if (value.length >= 3) {
      const filtered = hospitalAddresses
        .map(a => (typeof a === 'string' ? a : a.name))
        .filter(name => name.toLowerCase().includes(value.toLowerCase()));
      setFilteredHospitals(filtered);
      setShowHospitalDropdown(true);
    } else {
      setFilteredHospitals([]);
      setShowHospitalDropdown(false);
    }
  };

  const selectHospital = (hospital) => {
    setHospitalSearchTerm(hospital);
    setValue('patient_address', hospital);
    setShowHospitalDropdown(false);
    setFilteredHospitals([]);
  };

  const clearHospitalSearch = () => {
    setHospitalSearchTerm('');
    setValue('patient_address', '');
    setShowHospitalDropdown(false);
    setFilteredHospitals([]);
    setHospitalHighlightIndex(-1);
  };

  // Hospital dropdown klavye navigasyonu
  const handleHospitalKeyDown = (event) => {
    if (!showHospitalDropdown || filteredHospitals.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHospitalHighlightIndex(prev => 
          prev < filteredHospitals.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHospitalHighlightIndex(prev => 
          prev > 0 ? prev - 1 : filteredHospitals.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (hospitalHighlightIndex >= 0 && hospitalHighlightIndex < filteredHospitals.length) {
          selectHospital(filteredHospitals[hospitalHighlightIndex]);
          setHospitalHighlightIndex(-1);
        }
        break;
      case 'Escape':
        setShowHospitalDropdown(false);
        setHospitalHighlightIndex(-1);
        break;
    }
  };

  // Destination address search functions
  const handleDestinationSearch = (e) => {
    const value = e.target.value;
    setDestinationSearchTerm(value);
    
    if (value.length >= 3) {
      const filtered = hospitalAddresses
        .map(a => (typeof a === 'string' ? a : a.name))
        .filter(name => name.toLowerCase().includes(value.toLowerCase()));
      setFilteredDestinations(filtered);
      setShowDestinationDropdown(true);
    } else {
      setFilteredDestinations([]);
      setShowDestinationDropdown(false);
    }
  };

  const selectDestination = (address) => {
    setDestinationSearchTerm(address);
    setValue('destination_address', address);
    setShowDestinationDropdown(false);
    setFilteredDestinations([]);
  };

  const clearDestinationSearch = () => {
    setDestinationSearchTerm('');
    setValue('destination_address', '');
    setShowDestinationDropdown(false);
    setFilteredDestinations([]);
    setDestinationHighlightIndex(-1);
  };

  // Destination dropdown klavye navigasyonu
  const handleDestinationKeyDown = (event) => {
    if (!showDestinationDropdown || filteredDestinations.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setDestinationHighlightIndex(prev => 
          prev < filteredDestinations.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setDestinationHighlightIndex(prev => 
          prev > 0 ? prev - 1 : filteredDestinations.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (destinationHighlightIndex >= 0 && destinationHighlightIndex < filteredDestinations.length) {
          selectDestination(filteredDestinations[destinationHighlightIndex]);
          setDestinationHighlightIndex(-1);
        }
        break;
      case 'Escape':
        setShowDestinationDropdown(false);
        setDestinationHighlightIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(event.target)) {
        setShowHospitalDropdown(false);
      }
      if (destinationDropdownRef.current && !destinationDropdownRef.current.contains(event.target)) {
        setShowDestinationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch case details
  const { data: caseData, isLoading, error } = useQuery({
    queryKey: ['case', id],
    queryFn: () => api.get(endpoints.cases.getById(id)).then(res => {
      const caseDetails = res.data.data.case;
      return caseDetails;
    }),
    enabled: !!id
  });

  // Vaka tamamlandıysa/iptal edildiyse 2. aşama sadece admin tarafından düzenlenebilir
  const isCaseLocked = ['completed', 'cancelled'].includes(caseData?.status);
  const canEditStage2 = isCaseLocked ? isAdmin : (canEdit && !isParamedic);

  // Fetch case activity logs (same endpoint, logs field)
  const { data: caseLogs = [] } = useQuery({
    queryKey: ['case-logs', id],
    queryFn: () => api.get(endpoints.cases.getById(id)).then(res => res.data.data?.logs || []),
    enabled: !!id,
    staleTime: 30000,
  });

  // Initialize caseGiverType from caseData when it loads
  // (must be after caseData declaration to avoid TDZ ReferenceError)
  useEffect(() => {
    if (caseData?.case_giver_type) {
      setCaseGiverType(caseData.case_giver_type);
      if (caseData.case_giver_type === 'presidency' && caseData.case_giver_presidency_id && presidencies.length > 0) {
        const pres = presidencies.find(p => p.id === caseData.case_giver_presidency_id);
        if (pres) setSelectedPresidency(pres);
      }
      if (caseData.case_giver_hospital) {
        setSelectedPresidencyHospital(caseData.case_giver_hospital);
      }
    }
  }, [caseData, presidencies]);

  // Derived KM metrics from case data (read-only view)
  const kmStart = caseData?.ambulance_km_start ?? null;
  const kmCaseArrived = caseData?.ambulance_km_case_arrived ?? null;
  const kmHospitalArrived = caseData?.ambulance_km_hospital_arrived ?? null;
  const kmEnd = caseData?.ambulance_km_end ?? null;

  const totalKmFromCase =
    kmStart != null && kmEnd != null && !isNaN(Number(kmStart)) && !isNaN(Number(kmEnd))
      ? Math.max(0, Number(kmEnd) - Number(kmStart))
      : null;

  const kmToCase =
    kmStart != null && kmCaseArrived != null && !isNaN(Number(kmStart)) && !isNaN(Number(kmCaseArrived))
      ? Math.max(0, Number(kmCaseArrived) - Number(kmStart))
      : null;

  // Fetch teams for editing
  const { data: teams, error: teamsError, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get(endpoints.cases.teams).then(res => {
      return res.data.teams || [];
    }),
    enabled: true, // Always fetch teams data
    retry: 1
  });

  // Function to fetch team assignment for auto-fill staff names
  const fetchTeamAssignment = useCallback(async (teamId, date) => {
    try {
      const response = await api.get(`/teams/${teamId}/daily/${date}`);
      return response.data.data;
    } catch (error) {
      console.error('Team assignment fetch error:', error);
      return null;
    }
  }, []);

  // Watch for team selection changes and other dynamic fields
  const selectedTeamId = watch('assigned_team_id');
  const selectedTransportType = watch('transport_type');
  const selectedPaymentType = watch('transport_payment_type');
  const selectedBillingInstitution = watch('billing_institution');
  const selectedStatus = watch('status');
  const kmStartValue = watch('ambulance_km_start');
  const kmEndValue = watch('ambulance_km_end');
  const totalKm =
    kmStartValue && kmEndValue && !isNaN(Number(kmStartValue)) && !isNaN(Number(kmEndValue))
      ? Math.max(0, Number(kmEndValue) - Number(kmStartValue))
      : null;
  const prevTeamIdRef = useRef(selectedTeamId);
  
  useEffect(() => {
    const fillStaffNames = async () => {
      // Only proceed if team actually changed (not just component re-render)
      if (selectedTeamId && selectedTeamId !== prevTeamIdRef.current && editingSection === 'stage3' && caseData) {
        // Fix date format - ensure it's YYYY-MM-DD string, not timestamp
        let targetDate = caseData.request_date || new Date().toISOString().split('T')[0];
        
        // Convert to proper date format if it's a timestamp or date object
        if (typeof targetDate === 'number' || targetDate instanceof Date) {
          targetDate = new Date(targetDate).toISOString().split('T')[0];
        }
        
        // Ensure it's in YYYY-MM-DD format
        if (targetDate && targetDate.includes('T')) {
          targetDate = targetDate.split('T')[0];
        }
        
        const teamAssignment = await fetchTeamAssignment(parseInt(selectedTeamId), targetDate);
        
        if (teamAssignment) {
          // Auto-fill staff names if they exist in daily assignment
          let fieldsUpdated = 0;
          if (teamAssignment.staff_1_name) {
            setValue('staff_1_name', teamAssignment.staff_1_name);
            fieldsUpdated++;
          }
          if (teamAssignment.staff_2_name) {
            setValue('staff_2_name', teamAssignment.staff_2_name);
            fieldsUpdated++;
          }
          if (teamAssignment.staff_3_name) {
            setValue('staff_3_name', teamAssignment.staff_3_name);
            fieldsUpdated++;
          }
          if (teamAssignment.ambulance_plate) {
            setValue('ambulance_plate', teamAssignment.ambulance_plate);
            fieldsUpdated++;
          }

          // Only show toast if fields were actually updated
          if (fieldsUpdated > 0) {
            toast({
              title: "Ekip bilgileri dolduruldu",
              description: `${teamAssignment.team_code} ekibinin personel bilgileri forma dolduruldu.`,
              variant: "success",
            });
          }
        }
      }
      
      // Update the previous team ID reference
      prevTeamIdRef.current = selectedTeamId;
    };

    fillStaffNames();
  }, [selectedTeamId, editingSection, caseData, setValue, toast, fetchTeamAssignment]);

  // Auto-fill team staff when team is selected in stage2 (for stage3 fields)
  const prevTeamIdForStage2Ref = useRef(selectedTeamId);
  
  useEffect(() => {
    const fillTeamStaffFromStage2 = async () => {
      // Only proceed if team actually changed and we're in stage2
      if (selectedTeamId && selectedTeamId !== prevTeamIdForStage2Ref.current && editingSection === 'stage2' && caseData) {
        // Fix date format - ensure it's YYYY-MM-DD string, not timestamp
        let targetDate = caseData.request_date || new Date().toISOString().split('T')[0];
        
        // Convert to proper date format if it's a timestamp or date object
        if (typeof targetDate === 'number' || targetDate instanceof Date) {
          targetDate = new Date(targetDate).toISOString().split('T')[0];
        }
        
        // Ensure it's in YYYY-MM-DD format
        if (targetDate && targetDate.includes('T')) {
          targetDate = targetDate.split('T')[0];
        }
        
        const teamAssignment = await fetchTeamAssignment(parseInt(selectedTeamId), targetDate);
        
        if (teamAssignment) {
          // Auto-fill stage3 fields immediately when team is selected in stage2
          let fieldsUpdated = 0;
          if (teamAssignment.staff_1_name) {
            setValue('staff_1_name', teamAssignment.staff_1_name);
            fieldsUpdated++;
          }
          if (teamAssignment.staff_2_name) {
            setValue('staff_2_name', teamAssignment.staff_2_name);
            fieldsUpdated++;
          }
          if (teamAssignment.staff_3_name) {
            setValue('staff_3_name', teamAssignment.staff_3_name);
            fieldsUpdated++;
          }
          if (teamAssignment.ambulance_plate) {
            setValue('ambulance_plate', teamAssignment.ambulance_plate);
            fieldsUpdated++;
          }

          // Only show toast if team has assignment data
          if (fieldsUpdated > 0) {
            toast({
              title: "Ekip seçildi",
              description: `${teamAssignment.team_code} ekibi seçildi. Personel bilgileri 3. aşamada görüntülenecek.`,
              variant: "success",
            });
          }
        }
      }
      
      // Update the previous team ID reference
      prevTeamIdForStage2Ref.current = selectedTeamId;
    };

    fillTeamStaffFromStage2();
  }, [selectedTeamId, editingSection, caseData, toast, fetchTeamAssignment]);

  // Auto-fill staff when switching to stage3 if team is already assigned
  useEffect(() => {
    const autoFillOnStage3Switch = async () => {
      if (editingSection === 'stage3' && caseData?.assigned_team_id) {
        // Check if staff fields are empty
        const currentStaff1 = getStaffName(caseData, 1) || caseData.staff_1_name;
        const currentStaff2 = getStaffName(caseData, 2) || caseData.staff_2_name;
        const currentStaff3 = getStaffName(caseData, 3) || caseData.staff_3_name;
        const currentPlate = caseData.ambulance_plate;
        
        // If any staff fields are empty, try to fill from team assignment
        if (!currentStaff1 || !currentStaff2 || !currentStaff3 || !currentPlate) {
          // Fix date format
          let targetDate = caseData.request_date || new Date().toISOString().split('T')[0];
          
          if (typeof targetDate === 'number' || targetDate instanceof Date) {
            targetDate = new Date(targetDate).toISOString().split('T')[0];
          }
          
          if (targetDate && targetDate.includes('T')) {
            targetDate = targetDate.split('T')[0];
          }
          
          const teamAssignment = await fetchTeamAssignment(caseData.assigned_team_id, targetDate);
          
          if (teamAssignment) {
            
            // Fill staff fields
            let fieldsUpdated = 0;
            if (teamAssignment.staff_1_name) {
              setValue('staff_1_name', teamAssignment.staff_1_name);
              fieldsUpdated++;
            }
            if (teamAssignment.staff_2_name) {
              setValue('staff_2_name', teamAssignment.staff_2_name);
              fieldsUpdated++;
            }
            if (teamAssignment.staff_3_name) {
              setValue('staff_3_name', teamAssignment.staff_3_name);
              fieldsUpdated++;
            }
            if (teamAssignment.ambulance_plate) {
              setValue('ambulance_plate', teamAssignment.ambulance_plate);
              fieldsUpdated++;
            }
            
            // Only show toast if fields were actually updated
            if (fieldsUpdated > 0) {
              toast({
                title: "Ekip bilgileri dolduruldu",
                description: "3. aşama açıldığında ekip bilgileri otomatik dolduruldu.",
                variant: "success",
              });
            }
          }
        }
      }
    };

    autoFillOnStage3Switch();
  }, [editingSection, caseData, setValue, toast, fetchTeamAssignment, getStaffName]);

  // Fetch departments for editing
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get(endpoints.cases.departments).then(res => res.data.data)
  });

  // Get department ID when billing institution is a başkanlık
  const getBillingDepartmentId = () => {
    if (!selectedBillingInstitution || !departments?.length) return null;
    
    const department = departments.find(d => d.name === selectedBillingInstitution);
    return department?.id || null;
  };

  // Fetch source hospitals (filtered by billing institution if it's a başkanlık)
  const { data: sourceHospitals = [], error: sourceHospitalsError, isLoading: sourceHospitalsLoading } = useQuery({
    queryKey: ['sourceHospitals', getBillingDepartmentId()],
    queryFn: async () => {
      const departmentId = getBillingDepartmentId();
      const params = departmentId ? { billing_department_id: departmentId } : {};
      const response = await api.get(endpoints.cases.sourceHospitals, { params });
      return response.data.data || [];
    },
    retry: 1
  });

  // Fetch destination hospitals (all hospitals, no filtering)
  const { data: destinationHospitals = [], error: destinationHospitalsError, isLoading: destinationHospitalsLoading } = useQuery({
    queryKey: ['destinationHospitals'],
    queryFn: () => api.get(endpoints.cases.destinationHospitals).then(res => {
      return res.data.data || [];
    }),
    retry: 1
  });

  // Medical data queries for stage3
  const { data: allDiagnoses = [] } = useQuery({
    queryKey: ['medical-diagnoses'],
    queryFn: () => api.get('/medical/diagnoses').then(r => r.data?.data || []),
  });

  const { data: allMedications = [] } = useQuery({
    queryKey: ['medical-medications'],
    queryFn: () => api.get('/medical/medications').then(r => r.data?.data || []),
  });

  const { data: allSupplies = [] } = useQuery({
    queryKey: ['medical-supplies'],
    queryFn: () => api.get('/medical/supplies').then(r => r.data?.data || []),
  });

  // Update mutations for each stage
  const updateStage1Mutation = useMutation({
    mutationFn: (data) => api.put(endpoints.cases.update(id), data),
    onSuccess: () => {
      // Tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['case', id]);
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teams']); // Takım atamaları
      queryClient.invalidateQueries(['teamAssignments']); // Günlük atamalar
      setEditingSection(null);
      toast({ title: "Başarılı", description: "1. aşama bilgileri güncellendi", variant: "success" });
    },
    onError: (error) => {
      toast({ title: "Hata", description: "Güncelleme sırasında hata oluştu", variant: "destructive" });
    }
  });

  const updateStage2Mutation = useMutation({
    mutationFn: (data) => {
      return api.put(endpoints.cases.updateStage2(id), data);
    },
    onSuccess: (response) => {
      console.log('✅ Stage 2 update successful', response);
      if (response.data && response.data.notificationResult) {
        console.log('🔔 Backend Notification Result:', response.data.notificationResult);
      }
      // Tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['case', id]);
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teams']); // Takım atamaları
      queryClient.invalidateQueries(['teamAssignments']); // Günlük atamalar
      setEditingSection(null);
      toast({ title: "Başarılı", description: "2. aşama bilgileri güncellendi", variant: "success" });
    },
    onError: (error) => {
      console.error('❌ Stage 2 update error:', error);
      const details = error?.response?.data?.details;
      const fieldMsg = details?.length
        ? `Alan: "${details[0].field}" — ${details[0].message}`
        : (error?.response?.data?.error || 'Güncelleme sırasında hata oluştu');
      toast({ title: "Hata", description: fieldMsg, variant: "destructive" });
    }
  });

  const updateStage3Mutation = useMutation({
    mutationFn: (data) => {
      return api.put(endpoints.cases.updateStage3(id), data);
    },
    onSuccess: () => {
      // Tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['case', id]);
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teams']); // Takım atamaları
      queryClient.invalidateQueries(['teamAssignments']); // Günlük atamalar
      setEditingSection(null);
      toast({ title: "Başarılı", description: "3. aşama bilgileri güncellendi", variant: "success" });
    },
    onError: (error) => {
      console.error('Stage 3 update error:', error);
      toast({ title: "Hata", description: "Güncelleme sırasında hata oluştu", variant: "destructive" });
    }
  });

  const onSubmit = (data) => {
    switch (editingSection) {
      case 'stage1':
        // Paramedic users can only edit specific fields in stage 1
        if (isParamedic) {
          const stage1Data = {
            patient_name: data.patient_name,
            patient_surname: data.patient_surname,
            patient_nationality_type: data.patient_nationality_type,
            patient_id_number: data.patient_id_number,
            patient_age: data.patient_age
          };
          updateStage1Mutation.mutate(stage1Data);
        } else {
          // Vakayı Veren alanlarını ve Faturalanan Kurum'u ekle
          const stage1Payload = {
            ...data,
            case_giver_type: caseGiverType,
            case_giver_person: caseGiverType === 'person' ? data.case_giver_person : null,
            case_giver_institution: caseGiverType === 'institution' ? data.case_giver_institution :
                                    caseGiverType === 'presidency' && selectedPresidency ? selectedPresidency.name : null,
            case_giver_hospital: caseGiverType === 'presidency' ? selectedPresidencyHospital : null,
            case_giver_presidency_id: caseGiverType === 'presidency' && selectedPresidency ? selectedPresidency.id : null,
          };
          // 112 Başkanlığı seçiliyse billing_institution'ı başkanlık adıyla doldur
          if (caseGiverType === 'presidency' && selectedPresidency) {
            stage1Payload.billing_institution = selectedPresidency.name;
          }
          updateStage1Mutation.mutate(stage1Payload);
        }
        break;
      case 'stage2':
        // Paramedic users cannot edit Stage 2
        if (isParamedic) {
          toast({
            title: 'Yetki Hatası',
            description: 'Paramedik kullanıcıları 2. aşamayı düzenleyemez',
            variant: 'destructive',
          });
          return;
        }
        // Planned cases require a team assignment before saving
        if (caseData?.is_planned) {
          const teamIdVal = data.assigned_team_id && data.assigned_team_id.toString().trim();
          if (!teamIdVal) {
            toast({
              title: 'Ekip Ataması Zorunlu',
              description: 'Planlı vakalar için ekip ataması yapılmadan kaydedilemez.',
              variant: 'destructive',
            });
            return;
          }
        }
        // Add payment type and fee for stage2
        const stage2Data = {
          ...data,
          transport_payment_type: isPaidTransport ? 'paid' : 'free',
          transport_fee: isPaidTransport ? (data.transport_fee ? parseFloat(data.transport_fee) : null) : null,
          // Convert IDs from string to integer, handle empty strings properly
          assigned_team_id: data.assigned_team_id && data.assigned_team_id.toString().trim() !== '' 
            ? parseInt(data.assigned_team_id, 10) 
            : null,
          source_hospital_id: data.source_hospital_id && data.source_hospital_id.toString().trim() !== '' 
            ? parseInt(data.source_hospital_id, 10) 
            : null,
          destination_hospital_id: data.destination_hospital_id && data.destination_hospital_id.toString().trim() !== '' 
            ? parseInt(data.destination_hospital_id, 10) 
            : null,
          // Ensure protocol_number is properly formatted
          protocol_number: data.protocol_number && data.protocol_number.toString().trim() !== '' 
            ? data.protocol_number.toString().trim() 
            : null,
          // Seçilen bölge numarası
          billing_zone: data.billing_zone && data.billing_zone !== 'no_zones' 
            ? data.billing_zone 
            : null
        };
        // 3. aşamaya ait saatler 2. aşamadan gönderilmemeli — aksi halde backend'de silinirler
        delete stage2Data.arrival_time;
        delete stage2Data.delivery_time;
        console.log('Stage 2 data being sent:', stage2Data);
        updateStage2Mutation.mutate(stage2Data);
        break;
      case 'stage3':
        // Check if completing the case - documents are required
        const isCompleting = data.status === 'completed';
        if (isCompleting) {
          const hasDocument = caseData?.document_path && caseData?.document_filename;
          const hasAsosDocument = caseData?.asos_document_path && caseData?.asos_document_filename;
          
          if (!hasDocument || !hasAsosDocument) {
            const missingDocs = [];
            if (!hasDocument) missingDocs.push('Tamamlama Belgesi');
            if (!hasAsosDocument) missingDocs.push('ASOS Belgesi');
            
            toast({
              title: "Belgeler Eksik",
              description: `Vaka tamamlanmadan önce tüm belgeler yüklenmelidir. Eksik belgeler: ${missingDocs.join(', ')}`,
              variant: "destructive",
            });
            return; // Stop submission
          }
        }
        
        // Clean and prepare stage3 data - only send non-empty values
        const stage3Data = {};
        
        // Helper function to add field if it has a value
        const addField = (fieldName, value) => {
          if (value && value.toString().trim() !== '') {
            stage3Data[fieldName] = value;
          }
        };
        
        // Paramedic users: sınırlı alanlar ama KM alanlarını da gönderebilsin
        if (isParamedic) {
          addField('notification_time', data.notification_time);
          addField('arrival_time', data.arrival_time);
          addField('delivery_time', data.delivery_time);
          addField('handover_person', data.handover_person);
          addField('handover_title', data.handover_title);
          addField('special_notes', data.special_notes);
          addField('ambulance_km_start', data.ambulance_km_start);
          addField('ambulance_km_case_arrived', data.ambulance_km_case_arrived);
          addField('ambulance_km_hospital_arrived', data.ambulance_km_hospital_arrived);
          addField('ambulance_km_end', data.ambulance_km_end);
        } else {
          addField('arrival_time', data.arrival_time);
          addField('delivery_time', data.delivery_time);
          addField('ambulance_plate', data.ambulance_plate);
          addField('staff_1_name', data.staff_1_name);
          addField('staff_2_name', data.staff_2_name);
          addField('staff_3_name', data.staff_3_name);
          addField('handover_person', data.handover_person);
          addField('handover_title', data.handover_title);
          addField('special_notes', data.special_notes);
          addField('result_description', data.result_description);
          addField('completion_notes', data.completion_notes);
          addField('ambulance_km_start', data.ambulance_km_start);
          addField('ambulance_km_case_arrived', data.ambulance_km_case_arrived);
          addField('ambulance_km_hospital_arrived', data.ambulance_km_hospital_arrived);
          addField('ambulance_km_end', data.ambulance_km_end);
          // 'initiated' is not a valid stage3 status; advance to 'in_progress'
          const normalizedStatus = data.status === 'initiated' ? 'in_progress' : data.status;
          addField('status', normalizedStatus);
        }

        // Medical data - available for all roles
        stage3Data.diagnoses = caseDiagnoses;
        stage3Data.medications = caseMedications;
        stage3Data.supplies = caseSupplies;
        
        updateStage3Mutation.mutate(stage3Data);
        break;
    }
  };

  const startEditing = (section) => {
    if (!caseData) return;
    setEditingSection(section);

    if (section === 'stage1') {
      reset({
        patient_name: caseData.patient_name || '',
        patient_surname: caseData.patient_surname || '',
        patient_birth_year: caseData.patient_birth_year || '',
        patient_age: caseData.patient_age || '',
        patient_nationality_type: caseData.patient_nationality_type || 'TC',
        patient_id_number: caseData.patient_id_number || '',
        patient_address: caseData.patient_address || '',
        patient_address_details: caseData.patient_address_details || '',
        initial_description: caseData.initial_description || '',
        requesting_authority: caseData.requesting_authority || '',
        call_giver_authority: caseData.call_giver_authority || '',
        case_giver_type: caseData.case_giver_type || 'person',
        case_giver_person: caseData.case_giver_person || '',
        case_giver_institution: caseData.case_giver_institution || '',
        case_giver_hospital: caseData.case_giver_hospital || '',
      }, { keepDefaultValues: true });

      // Initialize vakayı veren states from existing case data
      const giverType = caseData.case_giver_type || 'person';
      setCaseGiverType(giverType);
      if (giverType === 'presidency' && caseData.case_giver_presidency_id) {
        // Will be resolved after institutions load
      }

      if (caseData.patient_address) {
        setHospitalSearchTerm(caseData.patient_address);
      }
    } else {
      reset();
    }
    
    // Set different data based on section
    if (section === 'stage2') {
      // Stage 2 fields with proper type conversion
      const teamId = caseData.assigned_team_id;
      const sourceHospitalId = caseData.source_hospital_id;
      const destHospitalId = caseData.destination_hospital_id;
      
      setValue('assigned_team_id', teamId ? String(teamId) : '');
      setValue('transport_type', caseData.transport_type || '');
      setValue('protocol_number', caseData.protocol_number || '');
      setValue('source_hospital_id', sourceHospitalId ? String(sourceHospitalId) : '');
      setValue('destination_hospital_id', destHospitalId ? String(destHospitalId) : '');
      setValue('destination_address', caseData.destination_address || '');
      setValue('destination_address_details', caseData.destination_address_details || '');
      setValue('billing_institution', caseData.billing_institution || '');
      setValue('billing_zone', caseData.selected_zone ? String(caseData.selected_zone) : '');
      
      // Debug log for form values
      console.log('Form values set:', {
        teamId: teamId ? String(teamId) : '',
        sourceHospitalId: sourceHospitalId ? String(sourceHospitalId) : '',
        destHospitalId: destHospitalId ? String(destHospitalId) : '',
        transportType: caseData.transport_type,
        selectedZone: caseData.selected_zone
      });
      setValue('notification_time', caseData.notification_time || '');
      setValue('transport_payment_type', caseData.transport_payment_type || '');
      setValue('transport_fee', caseData.transport_fee || '');
      
      // Set payment type state
      setIsPaidTransport(caseData.transport_payment_type === 'paid');

      if (caseData.destination_address) {
        setDestinationSearchTerm(caseData.destination_address);
      }
    } else if (section === 'stage3') {
      // Stage 3 fields
      setValue('arrival_time', caseData.arrival_time || '');
      setValue('delivery_time', caseData.delivery_time || '');
      setValue('ambulance_plate', caseData.ambulance_plate || '');
      setValue('handover_person', caseData.handover_person || '');
      setValue('handover_title', caseData.handover_title || '');
      setValue('special_notes', caseData.special_notes || '');
      setValue('result_description', caseData.result_description || '');
      setValue('completion_notes', caseData.completion_notes || '');
      // Stage 3 cannot have 'initiated' status — advance to 'in_progress'
      setValue('status', caseData.status === 'initiated' ? 'in_progress' : (caseData.status || 'in_progress'));
      setValue('ambulance_km_start', caseData.ambulance_km_start ?? '');
      setValue('ambulance_km_case_arrived', caseData.ambulance_km_case_arrived ?? '');
      setValue('ambulance_km_hospital_arrived', caseData.ambulance_km_hospital_arrived ?? '');
      setValue('ambulance_km_end', caseData.ambulance_km_end ?? '');
      
      // Set staff names using helper function or from case data
      setValue('staff_1_name', getStaffName(caseData, 1) || caseData.staff_1_name || '');
      setValue('staff_2_name', getStaffName(caseData, 2) || caseData.staff_2_name || '');
      setValue('staff_3_name', getStaffName(caseData, 3) || caseData.staff_3_name || '');
      
      // Load medical data
      setCaseDiagnoses(caseData.diagnoses || []);
      setCaseMedications(caseData.medications || []);
      setCaseSupplies(caseData.supplies || []);

      const currentStaffData = {
        assigned_team_id: caseData.assigned_team_id,
        staff_1_name: caseData.staff_1_name,
        staff_2_name: caseData.staff_2_name,
        staff_3_name: caseData.staff_3_name,
        ambulance_plate: caseData.ambulance_plate
      };
      
      // Auto-fill team assignment data if team is assigned
      if (caseData.assigned_team_id) {
        const fillFromTeamAssignment = async () => {
          // Fix date format - ensure it's YYYY-MM-DD string, not timestamp
          let targetDate = caseData.request_date || new Date().toISOString().split('T')[0];
          
          // Convert to proper date format if it's a timestamp or date object
          if (typeof targetDate === 'number' || targetDate instanceof Date) {
            targetDate = new Date(targetDate).toISOString().split('T')[0];
          }
          
          // Ensure it's in YYYY-MM-DD format
          if (targetDate && targetDate.includes('T')) {
            targetDate = targetDate.split('T')[0];
          }
          
          const teamAssignment = await fetchTeamAssignment(caseData.assigned_team_id, targetDate);
          
          if (teamAssignment) {
            // Fill fields with team assignment data
            if (teamAssignment.staff_1_name) {
              setValue('staff_1_name', teamAssignment.staff_1_name);
            }
            if (teamAssignment.staff_2_name) {
              setValue('staff_2_name', teamAssignment.staff_2_name);
            }
            if (teamAssignment.staff_3_name) {
              setValue('staff_3_name', teamAssignment.staff_3_name);
            }
            if (teamAssignment.ambulance_plate) {
              setValue('ambulance_plate', teamAssignment.ambulance_plate);
            }
            
            // Only show toast if any fields were actually filled
            let filledCount = 0;
            if (teamAssignment.staff_1_name) filledCount++;
            if (teamAssignment.staff_2_name) filledCount++;
            if (teamAssignment.staff_3_name) filledCount++;
            if (teamAssignment.ambulance_plate) filledCount++;
            
            if (filledCount > 0) {
              toast({
                title: "Ekip bilgileri dolduruldu",
                description: "Günlük atamalardan personel bilgileri otomatik dolduruldu.",
                variant: "success",
              });
            }
          }
        };
        fillFromTeamAssignment();
      }
    }
  };

  const cancelEditing = () => {
    setEditingSection(null);
    reset();
    // Clear hospital search
    setHospitalSearchTerm('');
    setShowHospitalDropdown(false);
    setFilteredHospitals([]);
    // Clear destination search
    setDestinationSearchTerm('');
    setShowDestinationDropdown(false);
    setFilteredDestinations([]);
    // Clear payment state
    setIsPaidTransport(false);
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0];
  };

  // Helper functions for formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      timeZone: 'Europe/Istanbul'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5); // HH:MM format
  };
  const formatNationality = (type) => (type === 'Foreign' ? 'Yabancı Uyruklu' : 'Türk Vatandaşı');

  // Format timestamp in Turkey time (GMT+3)
  const formatTurkeyDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const LOG_ACTION_INFO = {
    created:                  { label: 'Vaka Oluşturuldu',          color: 'bg-green-100 text-green-700 border-green-200',  dot: 'bg-green-500' },
    completed:                { label: 'Vaka Tamamlandı',           color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-600' },
    stage1_updated:           { label: '1. Aşama Güncellendi',      color: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
    stage2_completed:         { label: '2. Aşama Güncellendi',      color: 'bg-blue-100 text-blue-700 border-blue-200',    dot: 'bg-blue-500' },
    updated:                  { label: 'Güncellendi',               color: 'bg-gray-100 text-gray-600 border-gray-200',    dot: 'bg-gray-400' },
    deleted:                  { label: 'Vaka Silindi',              color: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-600' },
    cancelled:                { label: 'Vaka İptal Edildi',         color: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-600' },
    document_uploaded:        { label: 'Tamamlama Belgesi Yüklendi',color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
    document_deleted:         { label: 'Tamamlama Belgesi Silindi', color: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-500' },
    asos_document_uploaded:   { label: 'ASOS Belgesi Yüklendi',     color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
    asos_document_deleted:    { label: 'ASOS Belgesi Silindi',      color: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-500' },
    ek3_document_uploaded:    { label: 'EK-3 Belgesi Yüklendi',     color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
    ek3_document_deleted:     { label: 'EK-3 Belgesi Silindi',      color: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-500' },
  };

  const getActionInfo = (action) =>
    LOG_ACTION_INFO[action] || { label: action, color: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' };

  const getMimeFromFilename = (name) => {
    const ext = (name || '').split('.').pop()?.toLowerCase();
    const mime = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
    return mime[ext] || 'application/octet-stream';
  };

  const handleCaseFile = async ({ endpoint, filename, mode = 'preview', setLoading }) => {
    if (!endpoint) return;
    let previewWindow = null;
    try {
      if (mode === 'preview') {
        previewWindow = window.open('', '_blank');
      }
      setLoading?.(true);
      const response = await api.get(endpoint, { responseType: 'blob' });
      const fromHeader = (response.headers['content-type'] || response.headers['Content-Type'] || '').split(';')[0].trim();
      const fromFilename = getMimeFromFilename(filename);
      const contentType = (fromHeader && /^(image\/|application\/pdf)/.test(fromHeader)) ? fromHeader : fromFilename;
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);

      if (mode === 'download') {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank');
      }

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Case document action error:', error);
      let description = "Belge açılırken/indirilirken hata oluştu. Lütfen tekrar deneyin.";
      if (error.response?.status === 404) {
        description = "Bu vaka için sunucuda belge bulunamadı. Belge daha önce silinmiş veya yüklenmemiş olabilir.";
      }
      toast({
        title: "Hata",
        description,
        variant: "destructive",
      });
      if (previewWindow) {
        previewWindow.close();
      }
    } finally {
      setLoading?.(false);
    }
  };

  const handleFormKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  // Kurum-bölge fiyatları: seçili faturalanan kuruma göre zoneleri çek
  useEffect(() => {
    const loadInstitutionZones = () => {
      setInstitutionZones([]);
      if (!selectedBillingInstitution || !institutionsData.length) return;

      // İsimle birebir eşleşme
      let institution = institutionsData.find(
        inst => inst.name === selectedBillingInstitution
      );

      // Birebir eşleşme yoksa küçük/ büyük harf duyarsız eşleşme dene
      if (!institution) {
        const target = selectedBillingInstitution.toLowerCase();
        institution = institutionsData.find(
          inst => inst.name && inst.name.toLowerCase() === target
        );
      }

      if (!institution) {
        console.warn('Seçili faturalanan kurum için institutions kaydı bulunamadı:', selectedBillingInstitution);
        return;
      }

      const zones = [];
      for (let i = 1; i <= 15; i++) {
        const price = institution[`zone_${i}_price`];
        const name = institution[`zone_${i}_name`];
        const description = institution[`zone_${i}_description`];
        const locations = institution[`zone_${i}_locations`];

        const hasPrice =
          price !== null &&
          price !== undefined &&
          price !== '' &&
          !Number.isNaN(parseFloat(price));
        const hasAnyInfo = !!(name || description || locations);

        // Fiyat 0 olsa bile (veya sadece lokasyon/ad tanımlı olsa bile) bölgeyi göster
        if (hasPrice || hasAnyInfo) {
          zones.push({
            zone_id: i,
            zone_number: i,
            zone_name: name || `${i}. Bölge`,
            base_price: hasPrice ? parseFloat(price) : 0,
            zone_description: description || null,
            locations: locations || ''
          });
        }
      }

      setInstitutionZones(zones);
    };

    loadInstitutionZones();
  }, [selectedBillingInstitution, institutionsData]);

  // Kurum tam adı: "7. Bölge - Tuzla Devlet Hastanesi (Tuzla)" -> "Tuzla Devlet Hastanesi"
  const getInstitutionShortName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '';
    const match = fullName.match(/^\d+\.\s*Bölge\s*-\s*(.+?)\s*\([^)]+\)\s*$/);
    if (match && match[1]) return match[1].trim();
    if (fullName.includes(' - ')) return fullName.split(' - ').slice(1).join(' - ').trim();
    return fullName.trim();
  };

  const getTransportTypeLabel = (type) => {
    return transportTypesList.find(option => option.value === type)?.label || type;
  };

  // PDF sharing functions
  const handlePdfGenerate = useCallback((status, errorMessage) => {
    setPdfStatus(status);
    
    if (status === 'success') {
      toast({
        title: "PDF Başarıyla Oluşturuldu",
        description: "Vaka detayları PDF olarak indirildi.",
        variant: "success",
      });
    } else if (status === 'error') {
      toast({
        title: "PDF Oluşturma Hatası",
        description: errorMessage || "PDF oluşturulurken bir hata oluştu.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const generatePDF = useCallback(() => {
    if (pdfGeneratorRef) {
      pdfGeneratorRef.generatePDF();
    }
  }, [pdfGeneratorRef]);

  // WhatsApp paylaşım metni oluştur
  const generateWhatsAppText = useCallback(() => {
    if (!caseData) return '';
    
    const statusInfo = getStatusInfo(caseData.status);
    const progress = caseData.status === 'initiated' ? 33 : 
                    caseData.status === 'in_progress' ? 66 : 100;
    
    let text = `EMT AMBULANS CRM\n`;
    text += `Vaka Detayi\n`;
    text += `Vaka #${caseData.case_number} - ${formatDate(caseData.request_date)}\n\n`;
    text += `Durum: ${statusInfo.label}\n`;
    text += `Ilerleme: %${progress}\n\n`;
    
    // Stage 1
    text += `*1. Asama: Vaka Baslatma*\n`;
    text += `Talep Tarihi: ${formatDate(caseData.request_date)}\n`;
    text += `Cagri Saati: ${formatTime(caseData.request_time)}\n`;
    text += `Hasta: ${caseData.patient_name || ''} ${caseData.patient_surname || ''}\n`;
    if (caseData.patient_age) text += `Yas: ${caseData.patient_age}\n`;
    if (caseData.patient_birth_year) text += `Dogum Yili: ${caseData.patient_birth_year}\n`;
    if (caseData.patient_address) text += `Hasta Adresi: ${caseData.patient_address}\n`;
    if (caseData.patient_address_details) text += `Adres Detayi: ${caseData.patient_address_details}\n`;
    if (caseData.initial_description) text += `Aciklama: ${caseData.initial_description}\n`;
    if (caseData.call_giver_authority) text += `Cagriyi Veren: ${caseData.call_giver_authority}\n`;
    if (caseData.requesting_authority) text += `Vakayi Alan: ${caseData.requesting_authority}\n`;
    if (caseData.case_giver_type) {
      const giverLabels = { person: 'Kisi', institution: 'Kurum', organization: 'Organizasyon', presidency: '112 Baskanlik' };
      text += `Vakayi Veren Tipi: ${giverLabels[caseData.case_giver_type] || caseData.case_giver_type}\n`;
      if (caseData.case_giver_institution) text += `Vakayi Veren Kurum: ${caseData.case_giver_institution}\n`;
      if (caseData.case_giver_hospital) text += `Vakayi Veren Hastane: ${caseData.case_giver_hospital}\n`;
      if (caseData.case_giver_person && caseData.case_giver_type !== 'presidency') text += `Vakayi Veren Kisi: ${caseData.case_giver_person}\n`;
    }
    
    text += `\n`;
    
    // Stage 2
    if (caseData.status !== 'initiated') {
      text += `*2. Asama: Nakil Bilgileri*\n`;
      if (caseData.team_name) text += `Atanan Ekip: ${caseData.team_name}\n`;
      if (caseData.transport_type) text += `Sevk Turu: ${getTransportTypeLabel(caseData.transport_type)}\n`;
      if (caseData.protocol_number) text += `ASOS Protokol No: ${caseData.protocol_number}\n`;
      if (caseData.transport_payment_type) {
        text += `Nakil Turu: ${caseData.transport_payment_type === 'paid' ? 'Ucretli Nakil' : 'Ucretsiz Nakil'}\n`;
        if (caseData.transport_payment_type === 'paid' && caseData.transport_fee) {
          text += `Ucret: ${caseData.transport_fee} TL\n`;
        }
      }
      if (caseData.destination_address) text += `Gidecegi Adres: ${caseData.destination_address}\n`;
      if (caseData.destination_address_details) text += `Gidecegi Adres Detayi: ${caseData.destination_address_details}\n`;
      if (caseData.billing_institution) text += `Faturalanan Kurum: ${caseData.billing_institution}\n`;
      if (caseData.notification_time) text += `Bildirim Saati: ${formatTime(caseData.notification_time)}\n`;
      
      text += `\n`;
    }
    
    // Stage 3
    if (hasStage3Data(caseData)) {
      text += `*3. Asama: Tamamlama*\n`;
      if (caseData.notification_time) text += `Nakil Ekibe Bildirim: ${formatTime(caseData.notification_time)}\n`;
      if (caseData.arrival_time) text += `Vakaya Ulasim: ${formatTime(caseData.arrival_time)}\n`;
      if (caseData.delivery_time) text += `Vaka Teslim: ${formatTime(caseData.delivery_time)}\n`;
      if (caseData.ambulance_plate) text += `Ambulans Plakasi: ${caseData.ambulance_plate}\n`;
      if (caseData.staff_1_name) text += `Personel 1: ${caseData.staff_1_name}\n`;
      if (caseData.staff_2_name) text += `Personel 2: ${caseData.staff_2_name}\n`;
      if (caseData.staff_3_name) text += `Personel 3: ${caseData.staff_3_name}\n`;
      if (caseData.handover_person) text += `Teslim Alan: ${caseData.handover_person}\n`;
      if (caseData.handover_title) text += `Teslim Alan Unvan: ${caseData.handover_title}\n`;
      if (caseData.result_description) text += `Sonuc: ${caseData.result_description}\n`;
      if (caseData.completion_notes) text += `Tamamlama Notlari: ${caseData.completion_notes}\n`;
      if (caseData.special_notes) text += `Ozel Notlar: ${caseData.special_notes}\n`;
    }
    
    return text;
  }, [caseData, getStatusInfo, formatDate, formatTime, getTransportTypeLabel, hasStage3Data]);

  // WhatsApp'ta paylaş
  const shareToWhatsApp = useCallback(() => {
    const text = generateWhatsAppText();
    if (!text) {
      toast({
        title: "Hata",
        description: "Vaka bilgileri bulunamadı.",
        variant: "destructive",
      });
      return;
    }
    
    // WhatsApp Web URL'si oluştur
    const encodedText = encodeURIComponent(text);
    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    
    // Yeni sekmede aç
    window.open(whatsappUrl, '_blank');
    
    toast({
      title: "WhatsApp Paylaşımı",
      description: "WhatsApp'ta paylaşım sayfası açıldı.",
      variant: "success",
    });
  }, [generateWhatsAppText, toast]);

  const captureScreenshot = useCallback(() => {
    if (!caseData) return;
    
    try {
      // Sadece form aşama bilgilerini hedef al (3 kart)
      const formStagesContainer = document.querySelector('.grid.grid-cols-1.lg\\:grid-cols-2.gap-8');
      const stage3Container = document.querySelector('.mt-8.border.border-gray-300.rounded-lg.p-6');
      
      if (!formStagesContainer) {
        toast({
          title: "Hata",
          description: "Form aşama alanı bulunamadı.",
          variant: "destructive",
        });
        return;
      }

      // Geçici bir container oluştur
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '20px';
      tempContainer.style.width = '800px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      
      // Form aşamalarını kopyala
      const stagesClone = formStagesContainer.cloneNode(true);
      tempContainer.appendChild(stagesClone);
      
      // 3. aşama varsa ekle
      if (stage3Container) {
        const stage3Clone = stage3Container.cloneNode(true);
        tempContainer.appendChild(stage3Clone);
      }
      
      document.body.appendChild(tempContainer);

      // Ekran görüntüsünü al
      html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false,
        removeContainer: true
      }).then(canvas => {
        // Canvas'ı blob'a çevir
        canvas.toBlob(blob => {
          // Dosya adını oluştur
          const fileName = `Vaka_${caseData.case_number}_${formatDate(caseData.request_date)}_form_asamalari.png`;
          
          // Dosyayı indir
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          // Geçici container'ı temizle
          document.body.removeChild(tempContainer);
          
          toast({
            title: "Form Aşamaları İndirildi",
            description: "Vaka form aşamaları ekran görüntüsü olarak indirildi.",
            variant: "success",
          });
        }, 'image/png');
      }).catch(error => {
        console.error('Ekran görüntüsü hatası:', error);
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
        toast({
          title: "Ekran Görüntüsü Hatası",
          description: "Ekran görüntüsü alınırken bir hata oluştu.",
          variant: "destructive",
        });
      });
    } catch (error) {
      console.error('Ekran görüntüsü hatası:', error);
      toast({
        title: "Ekran Görüntüsü Hatası",
        description: "Ekran görüntüsü alınırken bir hata oluştu.",
        variant: "destructive",
      });
    }
  }, [caseData, toast]);

  // Add error boundary state for stage2
  const [stage2Error, setStage2Error] = useState(null);

  // Helper function to render stage2 form safely
  const renderStage2Form = () => {
    try {
      if (teamsLoading || sourceHospitalsLoading || destinationHospitalsLoading || !teams || !sourceHospitals || !destinationHospitals) {
        return (
          <div className="text-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Veriler yükleniyor...</p>
            {teamsError && <p className="text-xs text-red-500 mt-1">Teams yüklenemedi: {teamsError.message}</p>}
            {sourceHospitalsError && <p className="text-xs text-red-500 mt-1">Source hospitals yüklenemedi: {sourceHospitalsError.message}</p>}
            {destinationHospitalsError && <p className="text-xs text-red-500 mt-1">Destination hospitals yüklenemedi: {destinationHospitalsError.message}</p>}
            <div className="mt-2 text-xs text-gray-600">
              <p>Teams: {teams ? teams.length + ' adet' : 'yükleniyor...'}</p>
              <p>Source hospitals: {sourceHospitals ? sourceHospitals.length + ' adet' : 'yükleniyor...'}</p>
              <p>Destination hospitals: {destinationHospitals ? destinationHospitals.length + ' adet' : 'yükleniyor...'}</p>
              {billingInstitution && <p>Faturalanacak kurum: {billingInstitution}</p>}
              {getBillingDepartmentId() && <p>Başkanlık ID: {getBillingDepartmentId()}</p>}
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4" style={{ minHeight: '200px' }}>
          <div>
            <Label htmlFor="assigned_team_id" className="flex items-center gap-1">
              Atanan Ekip
              {caseData?.is_planned && (
                <span className="text-red-500 font-bold ml-1">*</span>
              )}
            </Label>
            {caseData?.is_planned && !selectedTeamId && (
              <p className="text-xs text-red-500 mb-1 font-medium">⚠️ Planlı vakalar için ekip ataması zorunludur</p>
            )}
            <div className="flex">
              <div className="flex-1">
                <Select 
                  value={selectedTeamId || ''}
                  onValueChange={(value) => {
                    setValue('assigned_team_id', value);
                  }}
                >
                  <SelectTrigger className={caseData?.is_planned && !selectedTeamId ? 'border-red-400 ring-1 ring-red-400' : ''}>
                    <SelectValue placeholder="Ekip seçin">
                      {(() => {
                        if (!selectedTeamId || !teams) return 'Ekip seçin';
                        const selectedTeam = teams.find(t => t.id.toString() === selectedTeamId.toString());
                        return selectedTeam ? `${selectedTeam.code} - ${selectedTeam.name} ${selectedTeam.current_ambulance_plate ? `(${selectedTeam.current_ambulance_plate})` : ''}` : 'Ekip seçin';
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[...(teams || [])].sort((a, b) => {
                      const parseCode = (code) => {
                        if (!code) return { prefix: '', num: 9999 };
                        const match = code.match(/^([A-Za-z\u00C0-\u024F]+)-?(\d+)$/);
                        if (match) return { prefix: match[1].toUpperCase(), num: parseInt(match[2], 10) };
                        return { prefix: code.toUpperCase(), num: 0 };
                      };
                      const ca = parseCode(a.code);
                      const cb = parseCode(b.code);
                      if (ca.prefix !== cb.prefix) return ca.prefix.localeCompare(cb.prefix, 'tr');
                      return ca.num - cb.num;
                    }).map((team) => (
                      <SelectItem key={team.id} value={team.id.toString()}>
                        {team.code} - {team.name} {team.current_ambulance_plate ? `(${team.current_ambulance_plate})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTeamId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setValue('assigned_team_id', '');
                  }}
                  className="ml-2 px-2"
                  title="Ekip seçimini kaldır"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Personel ve ambulans bilgileri 3. aşamada otomatik doldurulacaktır
            </p>
          </div>
          <div>
            <Label htmlFor="transport_type">Sevk Türü</Label>
            <Select 
              value={selectedTransportType || ''}
              onValueChange={(value) => setValue('transport_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sevk türü seçin">
                  {selectedTransportType ? getTransportTypeLabel(selectedTransportType) : 'Sevk türü seçin'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {transportTypesList.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="protocol_number">ASOS Protokol No</Label>
            <Input
              id="protocol_number"
              {...register('protocol_number')}
            />
          </div>

          
          <div>
            <Label htmlFor="transport_payment_type">Nakil Türü</Label>
            <Select 
              value={selectedPaymentType || ''}
              onValueChange={(value) => {
                setValue('transport_payment_type', value);
                setIsPaidTransport(value === 'paid');
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Nakil türü seçin">
                  {selectedPaymentType === 'paid' ? 'Ücretli Nakil' : selectedPaymentType === 'free' ? 'Ücretsiz Nakil' : 'Nakil türü seçin'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Ücretsiz Nakil</SelectItem>
                <SelectItem value="paid">Ücretli Nakil</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {isPaidTransport && (
            <div>
              <Label htmlFor="transport_fee">Nakil Ücreti (TL)</Label>
              <Input
                type="number"
                id="transport_fee"
                {...register('transport_fee')}
                placeholder="Nakil ücreti girin"
                min="0"
                step="0.01"
              />
            </div>
          )}

                      <div>
              <Label htmlFor="destination_address">Gideceği Adres</Label>
              <div className="relative" ref={destinationDropdownRef}>
                <div className="flex">
                  <Input
                    id="destination_address"
                    value={destinationSearchTerm}
                    onChange={(e) => {
                      handleDestinationSearch(e);
                      setDestinationHighlightIndex(-1);
                    }}
                    onKeyDown={handleDestinationKeyDown}
                    placeholder="Hastane/adres aramak için en az 3 harf yazın..."
                    className="flex-1"
                    autoComplete="off"
                  />
                  {destinationSearchTerm && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearDestinationSearch}
                      className="ml-2 px-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {showDestinationDropdown && filteredDestinations.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredDestinations.map((address, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm ${
                          index === destinationHighlightIndex 
                            ? 'bg-blue-100 text-blue-900' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          selectDestination(address);
                          setDestinationHighlightIndex(-1);
                        }}
                        onMouseEnter={() => setDestinationHighlightIndex(index)}
                      >
                        {address}
                      </button>
                    ))}
                  </div>
                )}
                
                {showDestinationDropdown && destinationSearchTerm.length >= 3 && filteredDestinations.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2">
                    <p className="text-sm text-gray-500">Eşleşen adres bulunamadı</p>
                  </div>
                )}
              </div>
              <div className="space-y-2 mt-2">
                <Label htmlFor="destination_address_details">Detay Açıklama</Label>
                <Textarea
                  id="destination_address_details"
                  {...register('destination_address_details')}
                  placeholder="Gideceği adres için detaylı açıklama girin..."
                  rows={3}
                />
              </div>
            </div>
          <div>
            <Label htmlFor="notification_time">Nakil Ekibe Bildirim Saati</Label>
            <Input
              type="time"
              id="notification_time"
              {...register('notification_time')}
              placeholder="HH:MM"
            />
          </div>
          <div>
            <Label htmlFor="billing_institution">Faturalanan Kurum</Label>
            <Select 
              value={selectedBillingInstitution || ''}
              onValueChange={(value) => setValue('billing_institution', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Faturalanan kurum seçin">
                  {selectedBillingInstitution || 'Faturalanan kurum seçin'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {billingInstitutions.map((institution) => (
                  <SelectItem key={institution} value={institution}>
                    {institution}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 space-y-2">
            <Label htmlFor="billing_zone">Bölge Seçimi</Label>
            <Select
              value={watch('billing_zone') || ''}
              onValueChange={(value) => setValue('billing_zone', value)}
              displayLabel={(() => {
                const zoneNum = watch('billing_zone');
                if (!zoneNum || zoneNum === 'no_zones' || !institutionZones.length) return '';
                const zone = institutionZones.find(z => String(z.zone_number ?? z.zone_id) === String(zoneNum));
                if (!zone) return zoneNum;
                const instName = getInstitutionShortName(selectedBillingInstitution);
                const mid = instName ? ` - ${instName}` : '';
                return `${zone.zone_number ?? zone.zone_id}. Bölge${mid} - ${zone.base_price ?? 0} TL${zone.locations ? ` (${zone.locations})` : ''}`;
              })()}
            >
              <SelectTrigger>
                <SelectValue placeholder="Bölge seçin" />
              </SelectTrigger>
              <SelectContent>
                {institutionZones.length > 0 ? (
                  institutionZones.map((zone) => {
                    const instName = getInstitutionShortName(selectedBillingInstitution);
                    const mid = instName ? ` - ${instName}` : '';
                    return (
                    <SelectItem
                      key={zone.zone_id || zone.zone_number}
                      value={String(zone.zone_number || zone.zone_id)}
                    >
                      {`${zone.zone_number || zone.zone_id}. Bölge${mid} - ${zone.base_price || 0} TL${
                        zone.locations ? ` (${zone.locations})` : ''
                      }`}
                    </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="no_zones" disabled>
                    Bu kurum için tanımlı bölge/fiyat bulunmuyor
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Seçilen bölgenin fiyatı bilgilendirme amaçlı gösterilir; otomatik fiyatlandırma ayrıca çalışır.
            </p>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Stage 2 rendering error:', error);
      setStage2Error(error);
      return (
        <div className="text-center py-4 text-red-600">
          <p>Stage 2 form rendering hatası:</p>
          <p className="text-sm">{error.message}</p>
          <button 
            onClick={() => setStage2Error(null)} 
            className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-sm"
          >
            Tekrar Dene
          </button>
        </div>
      );
    }
  };

  // Runtime error check
  if (runtimeError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">JavaScript Hatası</h3>
              <p className="text-muted-foreground mb-4">
                {runtimeError.message || 'Beklenmeyen bir hata oluştu'}
              </p>
              <div className="space-x-2">
                <Button variant="outline" onClick={() => {
                  setRuntimeError(null);
                  window.location.reload();
                }}>
                  Sayfayı Yenile
                </Button>
                <Button variant="ghost" onClick={() => navigate('/cases')}>
                  Vakalara Dön
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="ml-4 text-muted-foreground">Vaka detayları yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Enhanced error handling
  if (error) {
    console.error('Case fetch error:', error);
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">Vaka Yüklenemedi</h3>
              <p className="text-muted-foreground mb-4">
                {error.response?.data?.message || error.message || 'Bilinmeyen hata oluştu'}
              </p>
              <div className="space-x-2">
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Sayfayı Yenile
                </Button>
                <Button variant="ghost" onClick={() => navigate('/cases')}>
                  Vakalara Dön
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 text-warning mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-warning mb-2">Vaka Bulunamadı</h3>
              <p className="text-muted-foreground mb-4">
                #{id} numaralı vaka bulunamadı veya silinmiş olabilir.
              </p>
              <Button variant="ghost" onClick={() => navigate('/cases')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Vakalara Dön
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  let statusInfo, progress;
  
  try {
    statusInfo = getStatusInfo(caseData.status);
    progress = caseData.status === 'initiated' ? 33 : 
                  caseData.status === 'in_progress' ? 66 : 100;
  } catch (err) {
    console.error('Error processing case data:', err);
    setRuntimeError(err);
    return null;
  }

  // 2 saat deadline hesaplaması
  const calculateDeadlineInfo = () => {
    // Sadece tamamlanmamış vakalar için göster
    if (!caseData || caseData.status === 'completed' || caseData.status === 'cancelled') {
      return null;
    }

    const requestDate = caseData.request_date;
    const requestTime = caseData.request_time;

    if (!requestDate || !requestTime) return null;

    // Çağrı zamanını oluştur
    const [hours, minutes] = requestTime.split(':').map(Number);
    const callDateTime = new Date(requestDate);
    callDateTime.setHours(hours, minutes, 0, 0);

    // 2 saat ekle (deadline)
    const deadlineDateTime = new Date(callDateTime.getTime() + 2 * 60 * 60 * 1000);
    
    // Şu anki zaman
    const now = new Date();
    
    // Kalan süre (milisaniye)
    const remainingMs = deadlineDateTime.getTime() - now.getTime();
    const remainingMinutes = Math.floor(remainingMs / (1000 * 60));
    
    // Durum belirleme
    let status = 'normal'; // normal, warning, danger, expired
    let message = '';
    let bgColor = '';
    let textColor = '';
    let borderColor = '';

    if (remainingMs <= 0) {
      // Süre geçmiş
      const overdueMinutes = Math.abs(remainingMinutes);
      const overdueHours = Math.floor(overdueMinutes / 60);
      const overdueRestMinutes = overdueMinutes % 60;
      status = 'expired';
      message = overdueHours > 0 
        ? `⚠️ Süre ${overdueHours} saat ${overdueRestMinutes} dakika geçti!`
        : `⚠️ Süre ${overdueRestMinutes} dakika geçti!`;
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      borderColor = 'border-red-300';
    } else if (remainingMinutes <= 30) {
      // Son 30 dakika
      status = 'danger';
      message = `🔴 Tamamlamak için ${remainingMinutes} dakika kaldı!`;
      bgColor = 'bg-red-100';
      textColor = 'text-red-700';
      borderColor = 'border-red-300';
    } else if (remainingMinutes <= 60) {
      // Son 1 saat
      status = 'warning';
      message = `🟡 Tamamlamak için ${remainingMinutes} dakika kaldı`;
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      borderColor = 'border-yellow-300';
    } else {
      // Normal süre
      const remainingHours = Math.floor(remainingMinutes / 60);
      const restMinutes = remainingMinutes % 60;
      status = 'normal';
      message = `⏱️ Tamamlamak için ${remainingHours} saat ${restMinutes} dakika kaldı`;
      bgColor = 'bg-blue-50';
      textColor = 'text-blue-700';
      borderColor = 'border-blue-200';
    }

    return {
      status,
      message,
      bgColor,
      textColor,
      borderColor,
      deadlineTime: deadlineDateTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      remainingMinutes
    };
  };

  const deadlineInfo = calculateDeadlineInfo();

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/cases')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Vakalara Dön
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Vaka Detayı</h1>
            <p className="text-muted-foreground">
              Vaka #{caseData.case_number} - {formatDate(caseData.request_date)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Badge 
            variant="outline" 
            className={`${statusInfo.color} text-white border-0`}
          >
            {statusInfo.label}
          </Badge>
          
          {/* PDF Paylaşma Butonları */}
          <div className="flex items-center space-x-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={generatePDF}
              disabled={pdfStatus === 'generating'}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Download className="h-4 w-4 mr-2" />
              {pdfStatus === 'generating' ? 'PDF Oluşturuluyor...' : 'PDF İndir'}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={captureScreenshot}
              className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Ekran Görüntüsü
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={shareToWhatsApp}
              className="text-green-600 border-green-200 hover:bg-green-50"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp Paylaş
            </Button>
          </div>
        </div>
      </div>

      {/* Deadline Uyarısı */}
      {deadlineInfo && (
        <div className={`mb-4 p-4 rounded-lg border ${deadlineInfo.bgColor} ${deadlineInfo.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Timer className={`h-5 w-5 ${deadlineInfo.textColor}`} />
              <div>
                <p className={`font-semibold ${deadlineInfo.textColor}`}>
                  {deadlineInfo.message}
                </p>
                <p className={`text-sm ${deadlineInfo.textColor} opacity-80`}>
                  Çağrı Saati: {formatTime(caseData.request_time)} → Son Tamamlama: {deadlineInfo.deadlineTime}
                </p>
              </div>
            </div>
            {deadlineInfo.status === 'expired' && (
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-6 w-6 text-red-600 animate-pulse" />
                <span className="text-red-700 font-bold text-sm">SÜRESİ GEÇTİ!</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium">Vaka İlerlemesi</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-4 text-xs text-muted-foreground">
            <span>Başlatıldı</span>
            <span>Nakil Bilgileri</span>
            <span>Tamamlandı</span>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Toggle */}
      {caseLogs.length > 0 && (() => {
        const sortedLogs = [...caseLogs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const createdLog = sortedLogs.find(l => l.action === 'created');
        const lastLog = [...caseLogs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        const completedLog = [...caseLogs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .find(l => l.notes && (l.notes.includes('tamaml') || l.notes.includes('3. Aşama')));
        return (
          <div className="mb-6">
            {/* Summary bar - always visible */}
            <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
              {createdLog && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  <span className="font-medium text-gray-700">Oluşturan:</span>
                  {createdLog.user_name || '—'} · {formatTurkeyDateTime(createdLog.created_at)}
                </span>
              )}
              {lastLog && lastLog !== createdLog && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                  <span className="font-medium text-gray-700">Son İşlem:</span>
                  {lastLog.user_name || '—'} · {formatTurkeyDateTime(lastLog.created_at)}
                </span>
              )}
              {completedLog && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-600 inline-block" />
                  <span className="font-medium text-gray-700">Tamamlayan:</span>
                  {completedLog.user_name || '—'} · {formatTurkeyDateTime(completedLog.created_at)}
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowActivityLog(v => !v)}
                className="ml-auto flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
              >
                <History className="h-3.5 w-3.5" />
                İşlem Geçmişi
                {showActivityLog ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </div>

            {/* Expandable timeline */}
            {showActivityLog && (
              <div className="mt-2 border border-gray-200 rounded-lg bg-white divide-y divide-gray-100 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 flex items-center gap-2">
                  <History className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">İşlem Geçmişi</span>
                  <span className="ml-1 text-xs text-gray-400">({caseLogs.length} kayıt · Türkiye saatiyle)</span>
                </div>
                {[...caseLogs]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((log, idx) => {
                    const info = getActionInfo(log.action);
                    return (
                      <div key={log.id || idx} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                        <div className="mt-1 flex-shrink-0">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${info.dot}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${info.color}`}>
                              {info.label}
                            </span>
                            {log.user_name && (
                              <span className="text-xs text-gray-700 font-medium">
                                <User className="h-3 w-3 inline mr-0.5 text-gray-400" />
                                {log.user_name}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">
                              <Clock className="h-3 w-3 inline mr-0.5" />
                              {formatTurkeyDateTime(log.created_at)}
                            </span>
                          </div>
                          {log.notes && (
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{log.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })()}

      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleFormKeyDown}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stage 1: Vaka Başlatma */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <CardTitle>1. Aşama: Vaka Başlatma</CardTitle>
                </div>
                {((canEditStage1 && caseData.status !== 'cancelled') || (isSupervisorOrAdmin && caseData.status === 'cancelled')) && editingSection !== 'stage1' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditing('stage1')}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                ) : ((canEditStage1 && caseData.status !== 'cancelled') || (isSupervisorOrAdmin && caseData.status === 'cancelled')) && editingSection === 'stage1' ? (
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={updateStage1Mutation.isPending}
                      onClick={handleSubmit(onSubmit)}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingSection === 'stage1' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="request_date">Talep Tarihi (Değiştirilemez)</Label>
                      <Input
                        type="date"
                        id="request_date"
                        {...register('request_date')}
                        readOnly
                        className="bg-gray-50 cursor-not-allowed"
                      />
                      <p className="text-xs text-muted-foreground">
                        Talep tarihi değiştirilemez
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="request_time">Çağrı Saati (Değiştirilemez)</Label>
                      <Input
                        type="time"
                        id="request_time"
                        {...register('request_time')}
                        readOnly
                        className="bg-gray-50 cursor-not-allowed"
                      />
                      <p className="text-xs text-muted-foreground">
                        Çağrı saati değiştirilemez
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="patient_name">Hasta Adı</Label>
                      <Input
                        id="patient_name"
                        {...register('patient_name', { required: 'Hasta adı gerekli' })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="patient_surname">Hasta Soyadı</Label>
                      <Input
                        id="patient_surname"
                        {...register('patient_surname')}
                      />
                    </div>
                  </div>
                <div className="space-y-2">
                  <Label>Uyruk *</Label>
                  <div className="flex items-center space-x-6">
                    <label className={`flex items-center space-x-2 ${isParamedic ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        value="TC"
                        {...register('patient_nationality_type')}
                        checked={selectedNationalityType === 'TC'}
                        disabled={isParamedic}
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-sm font-medium">Türk Vatandaşı</span>
                    </label>
                    <label className={`flex items-center space-x-2 ${isParamedic ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        value="Foreign"
                        {...register('patient_nationality_type')}
                        checked={selectedNationalityType === 'Foreign'}
                        disabled={isParamedic}
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-sm font-medium">Yabancı Uyruklu</span>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient_id_number">
                    {selectedNationalityType === 'Foreign' ? 'Pasaport No *' : 'TC Kimlik No *'}
                  </Label>
                  <Input
                    id="patient_id_number"
                    {...register('patient_id_number', {
                      required: isParamedic ? false : (
                        selectedNationalityType === 'Foreign'
                          ? 'Pasaport numarası gerekli'
                          : 'TC Kimlik No gerekli'
                      ),
                      pattern: (!isParamedic && selectedNationalityType === 'TC') ? {
                        value: /^[1-9][0-9]{10}$/,
                        message: 'TC Kimlik No 11 haneli olmalı ve 0 ile başlamamalı'
                      } : undefined,
                      minLength: (!isParamedic && selectedNationalityType === 'Foreign') ? {
                        value: 5,
                        message: 'Pasaport numarası en az 5 karakter olmalı'
                      } : undefined,
                      maxLength: (!isParamedic && selectedNationalityType === 'Foreign') ? {
                        value: 20,
                        message: 'Pasaport numarası en fazla 20 karakter olmalı'
                      } : (!isParamedic ? {
                        value: 11,
                        message: 'TC Kimlik No 11 haneli olmalıdır'
                      } : undefined)
                    })}
                    placeholder={selectedNationalityType === 'Foreign' ? 'Pasaport numarasını girin' : 'TC Kimlik No girin'}
                    maxLength={selectedNationalityType === 'TC' ? 11 : 20}
                    className={errors.patient_id_number ? 'border-destructive' : ''}
                  />
                  {errors.patient_id_number && (
                    <p className="text-sm text-destructive">{errors.patient_id_number.message}</p>
                  )}
                </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="patient_age">Yaş</Label>
                      <Input
                        type="number"
                        id="patient_age"
                        {...register('patient_age', { 
                          min: { value: 0, message: 'Yaş 0 veya daha büyük olmalı' },
                          max: { value: 150, message: 'Geçerli bir yaş girin' }
                        })}
                        placeholder="Yaş"
                        min="0"
                        max="150"
                        onBlur={handleAgeBlur}
                      />
                    </div>
                    <div>
                      <Label htmlFor="patient_birth_year">Doğum Yılı</Label>
                      <Input
                        type="number"
                        id="patient_birth_year"
                        {...register('patient_birth_year', { 
                          min: { value: 1900, message: 'Geçerli bir yıl girin' },
                          max: { value: new Date().getFullYear(), message: 'Gelecek yıl olamaz' }
                        })}
                        placeholder="YYYY"
                        min="1900"
                        max={new Date().getFullYear()}
                        onBlur={handleBirthYearBlur}
                        readOnly={isParamedic}
                        className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="patient_address">Hasta Adresi</Label>
                    <div className="relative" ref={hospitalDropdownRef}>
                      <div className="flex">
                        <Input
                          id="patient_address"
                          value={hospitalSearchTerm}
                          onChange={(e) => {
                            handleHospitalSearch(e);
                            setHospitalHighlightIndex(-1);
                          }}
                          onKeyDown={handleHospitalKeyDown}
                          placeholder="Hastane/adres aramak için en az 3 harf yazın..."
                          className="flex-1"
                          readOnly={isParamedic}
                          disabled={isParamedic}
                          autoComplete="off"
                        />
                        {hospitalSearchTerm && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearHospitalSearch}
                            className="ml-2 px-2"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      {showHospitalDropdown && filteredHospitals.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredHospitals.map((hospital, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm ${
                                index === hospitalHighlightIndex 
                                  ? 'bg-blue-100 text-blue-900' 
                                  : 'hover:bg-gray-100'
                              }`}
                              onClick={() => {
                                selectHospital(hospital);
                                setHospitalHighlightIndex(-1);
                              }}
                              onMouseEnter={() => setHospitalHighlightIndex(index)}
                            >
                              {hospital}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {showHospitalDropdown && hospitalSearchTerm.length >= 3 && filteredHospitals.length === 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2">
                          <p className="text-sm text-gray-500">Eşleşen hastane bulunamadı</p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 mt-2">
                      <Label htmlFor="patient_address_details">Detay Açıklama</Label>
                      <Textarea
                        id="patient_address_details"
                        {...register('patient_address_details')}
                        placeholder="Hasta adresi için detaylı açıklama girin..."
                        rows={3}
                        readOnly={isParamedic}
                        className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="initial_description">Açıklama</Label>
                    <Textarea
                      id="initial_description"
                      {...register('initial_description')}
                      rows={3}
                      readOnly={isParamedic}
                      className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="call_giver_authority">Çağrıyı Veren Yetkili İsmi</Label>
                    <Input
                      id="call_giver_authority"
                      {...register('call_giver_authority')}
                      placeholder="Çağrıyı veren yetkili kişinin adını girin"
                      readOnly={isParamedic}
                      className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="requesting_authority">Vakayı Alan Yetkili İsmi</Label>
                    <Input
                      id="requesting_authority"
                      {...register('requesting_authority')}
                      readOnly={isParamedic}
                      className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                    />
                  </div>
                  {/* Vakayı Veren Section */}
                  <div className="pt-3 border-t">
                    <Label className="text-base font-semibold mb-3 block">Vakayı Veren</Label>
                    <div className="flex flex-wrap gap-4 mb-3">
                      {['person', 'institution', 'organization', 'presidency'].map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id={`cg_${type}`}
                            name="case_giver_type_edit"
                            value={type}
                            checked={caseGiverType === type}
                            onChange={(e) => {
                              setCaseGiverType(e.target.value);
                              setValue('case_giver_type', e.target.value);
                              if (e.target.value !== 'presidency') {
                                setSelectedPresidency(null);
                                setSelectedPresidencyHospital('');
                              }
                            }}
                            className="w-4 h-4 text-primary"
                          />
                          <Label htmlFor={`cg_${type}`}>
                            {type === 'person' && 'Kişi'}
                            {type === 'institution' && 'Kurum'}
                            {type === 'organization' && 'Organizasyon'}
                            {type === 'presidency' && '112 Başkanlıkları'}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {caseGiverType === 'person' && (
                      <Input
                        {...register('case_giver_person')}
                        placeholder="Vakayı veren kişinin adını girin"
                      />
                    )}
                    {caseGiverType === 'institution' && (
                      <Input
                        {...register('case_giver_institution')}
                        placeholder="Kurum adını girin"
                      />
                    )}
                    {caseGiverType === 'organization' && (
                      <select
                        value={selectedOrganizationId}
                        onChange={(e) => {
                          const org = activeOrganizations.find(o => String(o.id) === e.target.value);
                          setSelectedOrganizationId(e.target.value);
                          setSelectedOrganization(org || null);
                          if (org) {
                            setValue('case_giver_institution', org.organization_name);
                            setValue('organization_id', org.id);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Organizasyon seçin</option>
                        {activeOrganizations.map((org) => (
                          <option key={org.id} value={org.id}>{org.organization_name}</option>
                        ))}
                      </select>
                    )}
                    {caseGiverType === 'presidency' && (
                      <div className="space-y-2">
                        <select
                          value={selectedPresidency?.id || ''}
                          onChange={(e) => {
                            const pres = presidencies.find(p => String(p.id) === e.target.value);
                            setSelectedPresidency(pres || null);
                            if (pres) {
                              setValue('case_giver_institution', pres.name);
                              setValue('case_giver_presidency_id', pres.id);
                            }
                            setSelectedPresidencyHospital('');
                            setValue('case_giver_hospital', '');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Başkanlık seçin</option>
                          {presidencies.map((pres) => (
                            <option key={pres.id} value={pres.id}>{pres.name}</option>
                          ))}
                        </select>
                        {selectedPresidency && (
                          <select
                            value={selectedPresidencyHospital}
                            onChange={(e) => {
                              setSelectedPresidencyHospital(e.target.value);
                              setValue('case_giver_hospital', e.target.value);
                              setValue('case_giver_person', e.target.value);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="">Hastane seçin</option>
                            {presidencyHospitals.map((h) => (
                              <option key={h.id} value={h.name}>{h.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Talep Tarihi</p>
                      <p>{formatDate(caseData.request_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Çağrı Saati</p>
                      <p>{formatTime(caseData.request_time)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Hasta</p>
                    <p>{caseData.patient_name} {caseData.patient_surname}</p>
                    {(caseData.patient_age || caseData.patient_birth_year) && (
                      <p className="text-sm text-muted-foreground">
                        {caseData.patient_age && `${caseData.patient_age} yaş`}
                        {caseData.patient_birth_year && ` (${caseData.patient_birth_year})`}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Uyruk</p>
                    <p>{formatNationality(caseData.patient_nationality_type)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {caseData.patient_nationality_type === 'Foreign' ? 'Pasaport No' : 'TC Kimlik No'}
                    </p>
                    <p className="text-sm">
                      {caseData.patient_id_number ? caseData.patient_id_number : 'Belirtilmemiş'}
                    </p>
                  </div>
                  {caseData.patient_address && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Hasta Adresi</p>
                      <p className="text-sm">{caseData.patient_address}</p>
                      {caseData.patient_address_details && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <strong>Detay:</strong> {caseData.patient_address_details}
                        </p>
                      )}
                    </div>
                  )}
                  {caseData.initial_description && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Açıklama</p>
                      <p className="text-sm">{caseData.initial_description}</p>
                    </div>
                  )}
                  {caseData.call_giver_authority && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Çağrıyı Veren Yetkili</p>
                      <p className="text-sm">{caseData.call_giver_authority}</p>
                    </div>
                  )}
                  {caseData.requesting_authority && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Vakayı Alan Yetkili</p>
                      <p className="text-sm">{caseData.requesting_authority}</p>
                    </div>
                  )}
                  {caseData.case_giver_type && (
                    <div className="pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground">Vakayı Veren</p>
                      <p className="text-sm font-medium">
                        {caseData.case_giver_type === 'person' && 'Kişi'}
                        {caseData.case_giver_type === 'institution' && 'Kurum'}
                        {caseData.case_giver_type === 'organization' && 'Organizasyon'}
                        {caseData.case_giver_type === 'presidency' && '112 Başkanlığı'}
                      </p>
                      {caseData.case_giver_institution && (
                        <p className="text-sm text-blue-700">{caseData.case_giver_institution}</p>
                      )}
                      {caseData.case_giver_person && (
                        <p className="text-sm">{caseData.case_giver_person}</p>
                      )}
                      {caseData.case_giver_hospital && caseData.case_giver_type === 'presidency' && (
                        <p className="text-sm text-gray-600">Hastane: {caseData.case_giver_hospital}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stage 2: Nakil Bilgileri */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-primary" />
                  <CardTitle>2. Aşama: Nakil Bilgileri</CardTitle>
                </div>
                {canEditStage2 && editingSection !== 'stage2' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditing('stage2')}
                    disabled={false}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                ) : canEditStage2 && editingSection === 'stage2' ? (
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={updateStage2Mutation.isPending}
                      onClick={handleSubmit(onSubmit)}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingSection === 'stage2' ? (
                renderStage2Form()
              ) : (
                <div className="space-y-3">
                  {caseData.status === 'initiated' ? (
                    <div className="text-center text-muted-foreground py-8">
                      <Info className="h-8 w-8 mx-auto mb-2" />
                      <p>2. aşama henüz tamamlanmamış</p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Atanan Ekip</p>
                        <p>{caseData.team_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Sevk Türü</p>
                        <p>{getTransportTypeLabel(caseData.transport_type)}</p>
                      </div>
                      {caseData.protocol_number && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">ASOS Protokol No</p>
                          <p>{caseData.protocol_number}</p>
                        </div>
                      )}

                      {caseData.transport_payment_type && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Nakil Türü</p>
                          <p>{caseData.transport_payment_type === 'paid' ? 'Ücretli Nakil' : 'Ücretsiz Nakil'}</p>
                          {caseData.transport_payment_type === 'paid' && caseData.transport_fee && (
                            <p className="text-sm text-green-600 font-medium">{caseData.transport_fee} TL</p>
                          )}
                        </div>
                      )}

                      {caseData.destination_address && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Gideceği Adres</p>
                          <p className="text-sm">{caseData.destination_address}</p>
                          {caseData.destination_address_details && (
                            <p className="text-xs text-muted-foreground mt-1">
                              <strong>Detay:</strong> {caseData.destination_address_details}
                            </p>
                          )}
                        </div>
                      )}
                      {caseData.billing_institution && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Faturalanan Kurum</p>
                          <p>{caseData.billing_institution}</p>
                        </div>
                      )}
                      {caseData.organization_id && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Organizasyon</p>
                          <button
                            type="button"
                            className="text-primary hover:underline text-sm text-left"
                            onClick={() => navigate(`/organizations/${caseData.organization_id}`)}
                          >
                            {caseData.case_giver_type === 'organization' && caseData.case_giver_institution
                              ? caseData.case_giver_institution
                              : `Organizasyon #${caseData.organization_id}`}
                          </button>
                        </div>
                      )}
                      {caseData.notification_time && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Nakil Ekibe Bildirim Saati</p>
                          <p>{formatTime(caseData.notification_time)}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stage 3: Tamamlama - spans full width */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <CardTitle>3. Aşama: Tamamlama</CardTitle>
                </div>
                {((canEdit && caseData.status !== 'cancelled') || (isSupervisorOrAdmin && caseData.status === 'cancelled')) && editingSection !== 'stage3' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditing('stage3')}
                    disabled={false}
                    title={caseData.status === 'cancelled' && !isSupervisorOrAdmin ? 'İptal edilmiş vakalar düzenlenemez' : 'Bilgileri düzenle'}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                ) : canEdit && editingSection === 'stage3' ? (
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={updateStage3Mutation.isPending}
                      onClick={handleSubmit(onSubmit)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {updateStage3Mutation.isPending ? 'Kaydediliyor...' : 'Güncelle & Kaydet'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingSection === 'stage3' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold">Zaman Bilgileri</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="arrival_time">Vakaya Ulaşım Saati</Label>
                        <Input
                          type="time"
                          id="arrival_time"
                          {...register('arrival_time')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="delivery_time">Vaka Teslim Saati</Label>
                        <Input
                          type="time"
                          id="delivery_time"
                          {...register('delivery_time')}
                        />
                      </div>
                    </div>
                    
                    {!isParamedic && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          <strong>Not:</strong> "Nakil Ekibine Bildirim Saati" 2. aşamada düzenlenir ve burada otomatik gösterilir.
                        </p>
                      </div>
                    )}
                    
                    {isParamedic && (
                      <div>
                        <Label htmlFor="notification_time">Nakil Ekibine Bildirim Saati</Label>
                        <Input
                          type="time"
                          id="notification_time"
                          {...register('notification_time')}
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="ambulance_plate">Ambulans Plakası</Label>
                      <Input
                        id="ambulance_plate"
                        {...register('ambulance_plate')}
                        readOnly={isParamedic}
                        className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        2. aşamada seçilen ekibın günlük atamasından otomatik doldurulur
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="staff_1_name">Personel 1</Label>
                        <Input
                          id="staff_1_name"
                          {...register('staff_1_name')}
                          readOnly={isParamedic}
                          className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="staff_2_name">Personel 2</Label>
                        <Input
                          id="staff_2_name"
                          {...register('staff_2_name')}
                          readOnly={isParamedic}
                          className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="staff_3_name">Personel 3</Label>
                        <Input
                          id="staff_3_name"
                          {...register('staff_3_name')}
                          readOnly={isParamedic}
                          className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">
                      Personel bilgileri, 2. aşamada seçilen ekibın günlük atamasından otomatik doldurulur. 
                      Manuel değişiklik yapabilirsiniz.
                    </p>

                    <div className="mt-4 space-y-3">
                      <h4 className="font-semibold">Kilometre Bilgileri</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="ambulance_km_start">Vaka Çıkış KM</Label>
                          <Input
                            id="ambulance_km_start"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Örn: 86597"
                            {...register('ambulance_km_start')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ambulance_km_case_arrived">Vaka Varış KM</Label>
                          <Input
                            id="ambulance_km_case_arrived"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Örn: 86597"
                            {...register('ambulance_km_case_arrived')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ambulance_km_hospital_arrived">Hastaneye Varış KM</Label>
                          <Input
                            id="ambulance_km_hospital_arrived"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Örn: 86598"
                            {...register('ambulance_km_hospital_arrived')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ambulance_km_end">İstasyona Dönüş KM</Label>
                          <Input
                            id="ambulance_km_end"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Örn: 86598"
                            {...register('ambulance_km_end')}
                          />
                        </div>
                      </div>
                      {totalKm !== null && (
                        <p className="text-xs font-medium text-slate-700">
                          Toplam Mesafe: <span className="font-semibold">{totalKm}</span> km
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold">Nakil Bilgileri</h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-700">
                        <strong>Atanan Ekip:</strong> {caseData?.team_name || 'Henüz atanmamış'}
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Sevk Türü:</strong> {caseData?.transport_type ? getTransportTypeLabel(caseData.transport_type) : 'Henüz belirtilmemiş'}
                      </p>
                      {caseData?.notification_time && (
                        <p className="text-sm text-blue-700 mt-1">
                          <strong>Nakil Ekibe Bildirim Saati:</strong> {formatTime(caseData.notification_time)}
                        </p>
                      )}
                    </div>
                    
                    <h4 className="font-semibold">Teslim Bilgileri</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="handover_person">Teslim Alan Kişi</Label>
                        <Input
                          id="handover_person"
                          {...register('handover_person')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="handover_title">Unvanı</Label>
                        <Input
                          id="handover_title"
                          {...register('handover_title')}
                          placeholder="Dr., Hemşire, ATT vb."
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="special_notes">Özel Notlar</Label>
                      <Textarea
                        id="special_notes"
                        {...register('special_notes')}
                        rows={3}
                        placeholder="Özel durumlar, dikkat edilmesi gerekenler"
                      />
                    </div>
                  </div>

                  {/* Medical Data Section - full width */}
                  <div className="md:col-span-2 space-y-6 border-t pt-6">
                    {/* Teşhisler */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center space-x-2">
                        <Stethoscope className="h-4 w-4 text-medical-600" />
                        <span>Teşhisler</span>
                      </h4>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            value={diagnosisSearch}
                            onChange={(e) => {
                              setDiagnosisSearch(e.target.value);
                              setShowDiagnosisDropdown(e.target.value.length >= 1);
                            }}
                            onFocus={() => { if (diagnosisSearch.length >= 1) setShowDiagnosisDropdown(true); }}
                            onBlur={() => setTimeout(() => setShowDiagnosisDropdown(false), 200)}
                            placeholder="Teşhis kodu (ICD-10) veya adı..."
                            className="pl-9"
                            autoComplete="off"
                          />
                        </div>
                        {showDiagnosisDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                            {allDiagnoses
                              .filter(d => !caseDiagnoses.some(cd => cd.code === d.code))
                              .filter(d => {
                                const q = diagnosisSearch.toLowerCase();
                                return d.code.toLowerCase().includes(q) || d.name.toLowerCase().includes(q);
                              })
                              .slice(0, 10)
                              .map((d) => (
                                <button
                                  key={d.code}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setCaseDiagnoses(prev => [...prev, { code: d.code, name: d.name, description: d.description }]);
                                    setDiagnosisSearch('');
                                    setShowDiagnosisDropdown(false);
                                  }}
                                >
                                  <span className="font-medium text-medical-700">{d.code}</span>
                                  <span className="ml-2 text-sm text-slate-700">{d.name}</span>
                                  {d.description && <p className="text-xs text-slate-400 mt-0.5">{d.description}</p>}
                                </button>
                              ))}
                            {allDiagnoses.filter(d => !caseDiagnoses.some(cd => cd.code === d.code)).filter(d => {
                              const q = diagnosisSearch.toLowerCase();
                              return d.code.toLowerCase().includes(q) || d.name.toLowerCase().includes(q);
                            }).length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-400">Sonuç bulunamadı</div>
                            )}
                          </div>
                        )}
                      </div>
                      {caseDiagnoses.length > 0 && (
                        <div className="space-y-1">
                          {caseDiagnoses.map((d, idx) => (
                            <div key={d.code} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                              <div>
                                <span className="font-medium text-blue-800">{d.code}</span>
                                <span className="ml-2 text-sm text-slate-700">{d.name}</span>
                                {d.description && <p className="text-xs text-slate-500">{d.description}</p>}
                              </div>
                              <button type="button" onClick={() => setCaseDiagnoses(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* İlaçlar */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center space-x-2">
                        <Pill className="h-4 w-4 text-medical-600" />
                        <span>İlaçlar</span>
                      </h4>
                      <div className="relative">
                        <div className="relative">
                          <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            value={medicationSearch}
                            onChange={(e) => {
                              setMedicationSearch(e.target.value);
                              setShowMedicationDropdown(e.target.value.length >= 1);
                            }}
                            onFocus={() => { if (medicationSearch.length >= 1) setShowMedicationDropdown(true); }}
                            onBlur={() => setTimeout(() => setShowMedicationDropdown(false), 200)}
                            placeholder="İlaç ekle..."
                            className="pl-9"
                            autoComplete="off"
                          />
                        </div>
                        {showMedicationDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                            {allMedications
                              .filter(m => !caseMedications.some(cm => cm.name === m.name))
                              .filter(m => m.name.toLowerCase().includes(medicationSearch.toLowerCase()))
                              .slice(0, 10)
                              .map((m) => (
                                <button
                                  key={m.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setCaseMedications(prev => [...prev, { id: m.id, name: m.name, form: m.form, dose: m.default_dose || '' }]);
                                    setMedicationSearch('');
                                    setShowMedicationDropdown(false);
                                  }}
                                >
                                  <span className="text-sm font-medium text-slate-700">{m.name}</span>
                                  {m.form && <span className="ml-2 text-xs text-slate-400">({m.form})</span>}
                                </button>
                              ))}
                            {allMedications.filter(m => !caseMedications.some(cm => cm.name === m.name)).filter(m => m.name.toLowerCase().includes(medicationSearch.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-400">Sonuç bulunamadı</div>
                            )}
                          </div>
                        )}
                      </div>
                      {caseMedications.length > 0 && (
                        <div className="space-y-1">
                          {caseMedications.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
                              <div className="flex items-center space-x-3 flex-1">
                                <div>
                                  <span className="text-sm font-medium text-slate-700">{m.name}</span>
                                  {m.form && <span className="ml-1 text-xs text-slate-400">({m.form})</span>}
                                </div>
                                <Input
                                  value={m.dose || ''}
                                  onChange={(e) => setCaseMedications(prev => prev.map((item, i) => i === idx ? { ...item, dose: e.target.value } : item))}
                                  placeholder="Doz"
                                  className="h-7 w-32 text-xs"
                                />
                              </div>
                              <button type="button" onClick={() => setCaseMedications(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 ml-2">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Malzemeler */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center space-x-2">
                        <Package className="h-4 w-4 text-medical-600" />
                        <span>Malzemeler</span>
                      </h4>
                      <div className="relative">
                        <div className="relative">
                          <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            value={supplySearch}
                            onChange={(e) => {
                              setSupplySearch(e.target.value);
                              setShowSupplyDropdown(e.target.value.length >= 1);
                            }}
                            onFocus={() => { if (supplySearch.length >= 1) setShowSupplyDropdown(true); }}
                            onBlur={() => setTimeout(() => setShowSupplyDropdown(false), 200)}
                            placeholder="Malzeme ekle..."
                            className="pl-9"
                            autoComplete="off"
                          />
                        </div>
                        {showSupplyDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                            {allSupplies
                              .filter(s => !caseSupplies.some(cs => cs.name === s.name))
                              .filter(s => s.name.toLowerCase().includes(supplySearch.toLowerCase()))
                              .slice(0, 10)
                              .map((s) => (
                                <button
                                  key={s.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setCaseSupplies(prev => [...prev, { id: s.id, name: s.name, unit: s.unit, quantity: 1 }]);
                                    setSupplySearch('');
                                    setShowSupplyDropdown(false);
                                  }}
                                >
                                  <span className="text-sm font-medium text-slate-700">{s.name}</span>
                                  {s.unit && <span className="ml-2 text-xs text-slate-400">({s.unit})</span>}
                                </button>
                              ))}
                            {allSupplies.filter(s => !caseSupplies.some(cs => cs.name === s.name)).filter(s => s.name.toLowerCase().includes(supplySearch.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-400">Sonuç bulunamadı</div>
                            )}
                          </div>
                        )}
                      </div>
                      {caseSupplies.length > 0 && (
                        <div className="space-y-1">
                          {caseSupplies.map((s, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                              <div className="flex items-center space-x-3 flex-1">
                                <span className="text-sm font-medium text-slate-700">{s.name}</span>
                                <div className="flex items-center space-x-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={s.quantity || 1}
                                    onChange={(e) => setCaseSupplies(prev => prev.map((item, i) => i === idx ? { ...item, quantity: e.target.value } : item))}
                                    className="h-7 w-16 text-xs"
                                  />
                                  {s.unit && <span className="text-xs text-slate-400">{s.unit}</span>}
                                </div>
                              </div>
                              <button type="button" onClick={() => setCaseSupplies(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 ml-2">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold">Sonuç Bilgileri</h4>
                    
                    <div>
                      <Label htmlFor="status">Nakil Sonucu</Label>
                      <Select 
                        value={selectedStatus || ''}
                        onValueChange={(value) => {
                          setValue('status', value);
                        }}
                        disabled={isParamedic}
                      >
                        <SelectTrigger className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}>
                                                  <SelectValue placeholder="Nakil sonucunu seçin">
                          {selectedStatus === 'completed' ? 'Nakil Tamamlandı' : 
                           selectedStatus === 'cancelled' ? 'Nakil İptal' : 
                           selectedStatus === 'in_progress' ? 'Devam Ediyor' : 
                           'Nakil sonucunu seçin'}
                        </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                          <SelectItem value="completed">Nakil Tamamlandı</SelectItem>
                          <SelectItem value="cancelled">Nakil İptal</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Vaka durumunu seçin
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="result_description">Sonuç Açıklaması (Opsiyonel)</Label>
                      <Textarea
                        id="result_description"
                        {...register('result_description')}
                        rows={3}
                        placeholder="Nakil sonucu hakkında detaylı açıklama yazabilirsiniz..."
                        readOnly={isParamedic}
                        className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                      />
                    </div>
                    <div>
                      <Label htmlFor="completion_notes">Tamamlama Notları (Opsiyonel)</Label>
                      <Textarea
                        id="completion_notes"
                        {...register('completion_notes')}
                        rows={4}
                        placeholder="Tamamlama ile ilgili ek notlar yazabilirsiniz..."
                        readOnly={isParamedic}
                        className={isParamedic ? 'bg-gray-50 cursor-not-allowed' : ''}
                      />
                    </div>
                    
                    {/* Belge Yükleme */}
                    <div>
                      <Label>
                        Tamamlama Belgesi {selectedStatus === 'completed' && <span className="text-destructive">*</span>}
                      </Label>
                      {selectedStatus === 'completed' && (
                        <p className="text-xs text-muted-foreground mb-2">
                          Vaka tamamlanmadan önce yüklenmesi zorunludur.
                        </p>
                      )}
                      <DocumentUpload 
                        caseId={id}
                        currentDocument={caseData}
                        onDocumentChange={(documentData) => {
                          // Document güncellendiğinde case data'yı refresh et
                          queryClient.invalidateQueries(['case', id]);
                        }}
                      />
                    </div>
                    
                    {/* ASOS Belgesi Yükleme */}
                    <div>
                      <Label>
                        ASOS Belgesi {selectedStatus === 'completed' && <span className="text-destructive">*</span>}
                      </Label>
                      {selectedStatus === 'completed' && (
                        <p className="text-xs text-muted-foreground mb-2">
                          Vaka tamamlanmadan önce yüklenmesi zorunludur.
                        </p>
                      )}
                      <AsosDocumentUpload 
                        caseId={id}
                        currentDocument={caseData}
                        onDocumentChange={(documentData) => {
                          queryClient.invalidateQueries(['case', id]);
                        }}
                      />
                    </div>

                    {/* EK-3 Belgesi Yükleme */}
                    <div>
                      <Label>EK-3 Belgesi</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        112 Başkanlığı vakalarına ait EK-3 belgesi.
                      </p>
                      <Ek3DocumentUpload
                        caseId={id}
                        currentDocument={caseData}
                        readOnly={false}
                        onDocumentChange={() => {
                          queryClient.invalidateQueries(['case', id]);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {!hasStage3Data(caseData) ? (
                    <div>
                      <div className="text-center text-muted-foreground py-8">
                        <Info className="h-8 w-8 mx-auto mb-2" />
                        <p>3. aşama henüz tamamlanmamış</p>
                      </div>
                      {/* EK-3 belgesi: Başkanlık kullanıcısı 3. aşama tamamlanmadan da yönetebilir */}
                      {isPresidencyUser && (
                        <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30 mt-2">
                          <div className="flex items-center space-x-2 mb-3">
                            <FileText className="h-5 w-5 text-orange-500" />
                            <h4 className="font-semibold text-orange-800">EK-3 Belgesi</h4>
                          </div>
                          <Ek3DocumentUpload
                            caseId={id}
                            currentDocument={caseData}
                            readOnly={false}
                            onDocumentChange={() => queryClient.invalidateQueries(['case', id])}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Zaman Çizelgesi */}
                      {(caseData.notification_time || caseData.arrival_time || caseData.delivery_time) && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <Clock className="h-5 w-5 text-blue-600" />
                            <h4 className="font-semibold text-blue-900">Zaman Çizelgesi</h4>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {caseData.notification_time && (
                              <div className="bg-white rounded-lg p-3 border border-blue-100 shadow-sm">
                                <p className="text-xs font-medium text-blue-600 mb-1">1. Nakil Ekibine Bildirim</p>
                                <p className="text-lg font-bold text-blue-900">{formatTime(caseData.notification_time)}</p>
                              </div>
                            )}
                            {caseData.arrival_time && (
                              <div className="bg-white rounded-lg p-3 border border-green-100 shadow-sm">
                                <p className="text-xs font-medium text-green-600 mb-1">2. Vakaya Ulaşım</p>
                                <p className="text-lg font-bold text-green-900">{formatTime(caseData.arrival_time)}</p>
                              </div>
                            )}
                            {caseData.delivery_time && (
                              <div className="bg-white rounded-lg p-3 border border-purple-100 shadow-sm">
                                <p className="text-xs font-medium text-purple-600 mb-1">3. Vaka Teslim</p>
                                <p className="text-lg font-bold text-purple-900">{formatTime(caseData.delivery_time)}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Sol Sütun */}
                        <div className="space-y-4">
                          {/* Ambulans Bilgileri */}
                          {getCurrentAmbulancePlate() && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Truck className="h-5 w-5 text-gray-600" />
                                <h4 className="font-semibold text-gray-900">Ambulans Bilgileri</h4>
                              </div>
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-600 mb-1">Plaka</p>
                                <p className="text-lg font-bold text-gray-900 bg-white px-3 py-2 rounded border">{getCurrentAmbulancePlate()}</p>
                              </div>
                            </div>
                          )}

                          {/* Personel Bilgileri */}
                          {(getCurrentStaffName(1) || getCurrentStaffName(2) || getCurrentStaffName(3)) && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Users className="h-5 w-5 text-green-600" />
                                <h4 className="font-semibold text-green-900">Görevli Personel</h4>
                              </div>
                              <div className="space-y-2">
                                {getCurrentStaffName(1) && (
                                  <div className="flex items-center space-x-3 bg-white p-2 rounded border border-green-100">
                                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">1</div>
                                    <p className="font-medium text-green-900">{getCurrentStaffName(1)}</p>
                                  </div>
                                )}
                                {getCurrentStaffName(2) && (
                                  <div className="flex items-center space-x-3 bg-white p-2 rounded border border-green-100">
                                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">2</div>
                                    <p className="font-medium text-green-900">{getCurrentStaffName(2)}</p>
                                  </div>
                                )}
                                {getCurrentStaffName(3) && (
                                  <div className="flex items-center space-x-3 bg-white p-2 rounded border border-green-100">
                                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">3</div>
                                    <p className="font-medium text-green-900">{getCurrentStaffName(3)}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Sağ Sütun */}
                        <div className="space-y-4">
                          {/* Kilometre Bilgileri (Read-only) */}
                          {(kmStart != null || kmCaseArrived != null || kmHospitalArrived != null || kmEnd != null) && (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Truck className="h-5 w-5 text-slate-700" />
                                <h4 className="font-semibold text-slate-900">Kilometre Bilgileri</h4>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {kmStart != null && (
                                  <div className="bg-white rounded-md border border-slate-100 px-3 py-2">
                                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Vaka Çıkış KM</p>
                                    <p className="text-slate-900 font-semibold">{kmStart}</p>
                                  </div>
                                )}
                                {kmCaseArrived != null && (
                                  <div className="bg-white rounded-md border border-slate-100 px-3 py-2">
                                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Vaka Varış KM</p>
                                    <p className="text-slate-900 font-semibold">{kmCaseArrived}</p>
                                  </div>
                                )}
                                {kmHospitalArrived != null && (
                                  <div className="bg-white rounded-md border border-slate-100 px-3 py-2">
                                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Hastaneye Varış KM</p>
                                    <p className="text-slate-900 font-semibold">{kmHospitalArrived}</p>
                                  </div>
                                )}
                                {kmEnd != null && (
                                  <div className="bg-white rounded-md border border-slate-100 px-3 py-2">
                                    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">İstasyona Dönüş KM</p>
                                    <p className="text-slate-900 font-semibold">{kmEnd}</p>
                                  </div>
                                )}
                              </div>
                              {(kmToCase !== null || totalKmFromCase !== null) && (
                                <div className="mt-3 space-y-1 text-xs">
                                  {kmToCase !== null && (
                                    <p className="text-slate-700">
                                      <span className="font-semibold">Vakaya Ulaşılan Mesafe:</span> {kmToCase} km
                                    </p>
                                  )}
                                  {totalKmFromCase !== null && (
                                    <p className="text-slate-700">
                                      <span className="font-semibold">Toplam Gidilen Mesafe:</span> {totalKmFromCase} km
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Teslim Bilgileri */}
                          {(caseData.handover_person || caseData.handover_title) && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Building2 className="h-5 w-5 text-purple-600" />
                                <h4 className="font-semibold text-purple-900">Teslim Bilgileri</h4>
                              </div>
                              <div className="space-y-2">
                                {caseData.handover_person && (
                                  <div className="bg-white p-2 rounded border border-purple-100">
                                    <p className="text-xs text-purple-600 font-medium mb-1">Teslim Alan Kişi</p>
                                    <p className="font-semibold text-purple-900">{caseData.handover_person}</p>
                                  </div>
                                )}
                                {caseData.handover_title && (
                                  <div className="bg-white p-2 rounded border border-purple-100">
                                    <p className="text-xs text-purple-600 font-medium mb-1">Unvan</p>
                                    <p className="font-semibold text-purple-900">{caseData.handover_title}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tıbbi Veriler */}
                      {((caseData.diagnoses && caseData.diagnoses.length > 0) || (caseData.medications && caseData.medications.length > 0) || (caseData.supplies && caseData.supplies.length > 0)) && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {caseData.diagnoses && caseData.diagnoses.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Stethoscope className="h-5 w-5 text-blue-600" />
                                <h4 className="font-semibold text-blue-900">Teşhisler</h4>
                              </div>
                              <div className="space-y-1">
                                {caseData.diagnoses.map((d, idx) => (
                                  <div key={idx} className="bg-white p-2 rounded border border-blue-100">
                                    <span className="font-medium text-blue-800">{d.code}</span>
                                    <span className="ml-2 text-sm text-slate-700">{d.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {caseData.medications && caseData.medications.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Pill className="h-5 w-5 text-green-600" />
                                <h4 className="font-semibold text-green-900">İlaçlar</h4>
                              </div>
                              <div className="space-y-1">
                                {caseData.medications.map((m, idx) => (
                                  <div key={idx} className="bg-white p-2 rounded border border-green-100">
                                    <span className="text-sm font-medium text-slate-700">{m.name}</span>
                                    {m.dose && <span className="ml-2 text-xs text-slate-500">- {m.dose}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {caseData.supplies && caseData.supplies.length > 0 && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center space-x-2 mb-3">
                                <Package className="h-5 w-5 text-orange-600" />
                                <h4 className="font-semibold text-orange-900">Malzemeler</h4>
                              </div>
                              <div className="space-y-1">
                                {caseData.supplies.map((s, idx) => (
                                  <div key={idx} className="bg-white p-2 rounded border border-orange-100">
                                    <span className="text-sm font-medium text-slate-700">{s.name}</span>
                                    {s.quantity && <span className="ml-2 text-xs text-slate-500">x{s.quantity} {s.unit || ''}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Alt Kısım - Notlar ve Sonuç */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {caseData.special_notes && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center space-x-2 mb-3">
                              <AlertTriangle className="h-5 w-5 text-yellow-600" />
                              <h4 className="font-semibold text-yellow-900">Özel Notlar</h4>
                            </div>
                            <div className="bg-white p-3 rounded border border-yellow-100">
                              <p className="text-sm text-yellow-800 whitespace-pre-wrap leading-relaxed">{caseData.special_notes}</p>
                            </div>
                          </div>
                        )}

                        {(caseData.result_description || caseData.completion_notes || caseData.completed_at) && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center space-x-2 mb-3">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                              <h4 className="font-semibold text-green-900">Sonuç Bilgileri</h4>
                            </div>
                            <div className="space-y-3">
                              {caseData.result_description && (
                                <div className="bg-white p-3 rounded border border-green-100">
                                  <p className="text-xs text-green-600 font-medium mb-1">Sonuç</p>
                                  <p className="text-sm text-green-800 leading-relaxed">{caseData.result_description}</p>
                                </div>
                              )}
                              {caseData.completion_notes && (
                                <div className="bg-white p-3 rounded border border-green-100">
                                  <p className="text-xs text-green-600 font-medium mb-1">Tamamlama Notları</p>
                                  <p className="text-sm text-green-800 leading-relaxed">{caseData.completion_notes}</p>
                                </div>
                              )}
                              {caseData.completed_at && (
                                <div className="bg-white p-3 rounded border border-green-100">
                                  <p className="text-xs text-green-600 font-medium mb-1">Tamamlanma Tarihi</p>
                                  <p className="text-sm font-semibold text-green-900">
                                    {formatDate(caseData.completed_at)} {formatTime(caseData.completed_at)}
                                  </p>
                                </div>
                              )}
                              {/* Tamamlama Belgesi */}
                              {canEdit ? (
                                <div>
                                  <p className="text-xs text-green-600 font-medium mb-2">Tamamlama Belgesi</p>
                                  <DocumentUpload
                                    caseId={id}
                                    currentDocument={caseData}
                                    onDocumentChange={() => queryClient.invalidateQueries(['case', id])}
                                  />
                                </div>
                              ) : caseData.document_filename ? (
                                <div className="bg-white p-3 rounded border border-green-100">
                                  <p className="text-xs text-green-600 font-medium mb-1">Tamamlama Belgesi</p>
                                  <div className="flex items-center flex-wrap gap-2">
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-green-600" />
                                      <button
                                        type="button"
                                        onClick={() => handleCaseFile({ endpoint: endpoints.cases.getDocument(id), filename: caseData.document_filename, mode: 'preview' })}
                                        className="text-sm text-green-700 hover:text-green-900 hover:underline font-medium"
                                      >
                                        {caseData.document_filename}
                                      </button>
                                    </div>
                                    <Button type="button" size="sm" variant="outline" className="text-xs" disabled={isDocumentDownloading}
                                      onClick={() => handleCaseFile({ endpoint: endpoints.cases.getDocument(id), filename: caseData.document_filename, mode: 'download', setLoading: setIsDocumentDownloading })}
                                    >
                                      {isDocumentDownloading ? 'İndiriliyor...' : 'İndir'}
                                    </Button>
                                  </div>
                                  <p className="text-xs text-green-500 mt-1">
                                    {caseData.document_size && `${Math.round(caseData.document_size / 1024)} KB`}
                                    {caseData.document_upload_date && ` • Yükleme: ${formatDate(caseData.document_upload_date)}`}
                                  </p>
                                </div>
                              ) : null}

                              {/* ASOS Belgesi */}
                              {canEdit ? (
                                <div>
                                  <p className="text-xs text-orange-600 font-medium mb-2">ASOS Belgesi</p>
                                  <AsosDocumentUpload
                                    caseId={id}
                                    currentDocument={caseData}
                                    onDocumentChange={() => queryClient.invalidateQueries(['case', id])}
                                  />
                                </div>
                              ) : caseData.asos_document_filename ? (
                                <div className="bg-white p-3 rounded border border-orange-100">
                                  <p className="text-xs text-orange-600 font-medium mb-1">ASOS Belgesi</p>
                                  <div className="flex items-center flex-wrap gap-2">
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-orange-600" />
                                      <button
                                        type="button"
                                        onClick={() => handleCaseFile({ endpoint: endpoints.cases.getAsosDocument(id), filename: caseData.asos_document_filename, mode: 'preview' })}
                                        className="text-sm text-orange-700 hover:text-orange-900 hover:underline font-medium"
                                      >
                                        {caseData.asos_document_filename}
                                      </button>
                                    </div>
                                    <Button type="button" size="sm" variant="outline" className="text-xs" disabled={isAsosDocumentDownloading}
                                      onClick={() => handleCaseFile({ endpoint: endpoints.cases.getAsosDocument(id), filename: caseData.asos_document_filename, mode: 'download', setLoading: setIsAsosDocumentDownloading })}
                                    >
                                      {isAsosDocumentDownloading ? 'İndiriliyor...' : 'İndir'}
                                    </Button>
                                  </div>
                                  <p className="text-xs text-orange-500 mt-1">
                                    {caseData.asos_document_size && `${Math.round(caseData.asos_document_size / 1024)} KB`}
                                    {caseData.asos_document_upload_date && ` • Yükleme: ${formatDate(caseData.asos_document_upload_date)}`}
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* EK-3 Belgesi */}
                      {(caseData.ek3_document_filename || isPresidencyUser || canEdit) && (
                        <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
                          <div className="flex items-center space-x-2 mb-3">
                            <FileText className="h-5 w-5 text-orange-500" />
                            <h4 className="font-semibold text-orange-800">EK-3 Belgesi</h4>
                          </div>
                          <Ek3DocumentUpload
                            caseId={id}
                            currentDocument={caseData}
                            readOnly={!isPresidencyUser && !isAdmin && !canEdit}
                            onDocumentChange={() => {
                              queryClient.invalidateQueries(['case', id]);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
              </form>
        
        {/* PDF Generator - Hidden component */}
        {caseData && (
          <CasePdfGenerator
            ref={setPdfGeneratorRefCallback}
            caseData={caseData}
            onGenerate={handlePdfGenerate}
            transportTypes={transportTypesList}
          />
        )}
      </div>
    );
  };

export default CaseDetailPage; 